import React, { useState, useEffect, useCallback } from 'react';
import { useLocation} from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiPlus, 
  FiX,
  FiEye,
  FiFilter,
  FiEdit3,
  FiTrash2,
  FiChevronDown,
  FiShield,
  FiUser,     
  FiCheck,
  FiActivity
} from 'react-icons/fi';
import HealthLogService from '../../services/healthLogService';
import TopHeader from "../../../../common/Header/TopHeader";
import PatientMedicalClinicalData from '../../../../Patient/listPrincipalComponent/TableComponent/PatientMedicalClinicalData';
import PatientManagement from '../../../../common/Menu/PatientManagement';
import UniversalHealthForm from './UniversalHealthForm';

const VitalsTreatment = ({ onCountUpdate, componentName = 'VitalsTreatment' }) => {
  const location = useLocation();
  
  // **OBTENER DATOS DEL PACIENTE DESDE NAVIGATION STATE**
  const patientData = location.state;
  const patientId = patientData?.patientId;

  const [healthLogs, setHealthLogs] = useState([]);
  const [filteredHealthLogs, setFilteredHealthLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedHealthLog, setSelectedHealthLog] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showEditOffcanvas, setShowEditOffcanvas] = useState(false);
  const [editingHealthLog, setEditingHealthLog] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('vitals'); // Nuevo estado para categoría activa
const [allHealthData, setAllHealthData] = useState({}); // Almacenar todos los datos por categoría

  // Estado para Add New offcanvas
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
const [formState, setFormState] = useState({
  isOpen: false,
  category: null,
  editData: null,
  isSubmitting: false
});

  // Filter state
  const [filters, setFilters] = useState({
    bloodPressureRange: 'All',
    heartRateRange: 'All',
    temperatureRange: 'All',
    dateFrom: '',
    dateTo: '',
    status: 'All'
  });

  // **FUNCIÓN PRINCIPAL PARA CARGAR DATOS**
  const loadHealthLogs = useCallback(async () => {
  if (!patientId) {
    console.log('No patientId provided, component will show empty state');
    setHealthLogs([]);
    onCountUpdate && onCountUpdate(componentName, 0);
    return;
  }
  
  setIsLoading(true);
  try {
    console.log('Loading health logs for patient:', patientId);
    const response = await HealthLogService.getHealthLogsByUser(patientId);
    console.log('Raw API response:', response);
    
    let healthLogsData = [];
    
    if (response.data) {
      healthLogsData = Array.isArray(response.data) ? response.data : [response.data];
    } else if (Array.isArray(response)) {
      healthLogsData = response;
    }

    // **PROCESAR TODAS LAS CATEGORÍAS DE DATOS**
    const processedData = {
      vitals: [],
      bodyMeasurements: [],
      sugarProfile: [],
      liverProfile: [],
      kidneyProfile: [],
      lipidProfile: [],
      cycleTracking: [],
      pregnancyTracking: []
    };

    healthLogsData.forEach(healthLog => {
      // PROCESAR VITALES (código existente)
      if (healthLog.vitals && Array.isArray(healthLog.vitals)) {
        healthLog.vitals.forEach((vital, index) => {
          const vitalEntry = {
            // Primary identifiers
            vital_id: `${healthLog._id}_vital_${index}`,
            healthlog_id: healthLog._id,
            category: 'vitals',
            
            // Vital signs data
            systolic: vital.bloodPressure?.systolic || null,
            diastolic: vital.bloodPressure?.diastolic || null,
            heart_rate: vital.heartRate || null,
            temperature: vital.temperature || null,
            oxygen_saturation: vital.oxygenSaturation || null,
            respiratory_rate: vital.respiratoryRate || null,
            
            // Dates
            created_date: healthLog.createdAt || healthLog.created_date,
            updated_date: healthLog.updatedAt || healthLog.updated_date,
            
            // Patient info
            patient_id: patientId,
            patient_name: patientData?.patientName || 'Unknown Patient',
            
            // Status
            status: getVitalStatus(vital),
            
            // Preserve original
            _original: vital,
            _healthLog: healthLog
          };
          
          processedData.vitals.push(vitalEntry);
        });
      }

      // **PROCESAR MEDIDAS CORPORALES**
      if (healthLog.bodyMeasurements && Array.isArray(healthLog.bodyMeasurements)) {
        healthLog.bodyMeasurements.forEach((measurement, index) => {
          const measurementEntry = {
            measurement_id: `${healthLog._id}_measurement_${index}`,
            healthlog_id: healthLog._id,
            category: 'bodyMeasurements',
            
            // Body measurements data
            height: measurement.height || null,
            weight: measurement.weight || null,
            head_circumference: measurement.headCircumference || null,
            bmi: measurement.weight && measurement.height ? 
                 (measurement.weight / Math.pow(measurement.height/100, 2)).toFixed(1) : null,
            
            // Common fields
            created_date: healthLog.createdAt || healthLog.created_date,
            updated_date: healthLog.updatedAt || healthLog.updated_date,
            patient_id: patientId,
            patient_name: patientData?.patientName || 'Unknown Patient',
            status: getBodyMeasurementStatus(measurement),
            _original: measurement,
            _healthLog: healthLog
          };
          
          processedData.bodyMeasurements.push(measurementEntry);
        });
      }

      // **PROCESAR PERFIL DE AZÚCAR**
      if (healthLog.sugarProfile && Array.isArray(healthLog.sugarProfile)) {
        healthLog.sugarProfile.forEach((sugar, index) => {
          const sugarEntry = {
            sugar_id: `${healthLog._id}_sugar_${index}`,
            healthlog_id: healthLog._id,
            category: 'sugarProfile',
            
            // Sugar profile data
            fasting_blood_sugar: sugar.fastingBloodSugar || null,
            postprandial_sugar: sugar.postPrandialSugar || null,
            hba1c: sugar.hba1c || null,
            
            // Common fields
            created_date: healthLog.createdAt || healthLog.created_date,
            updated_date: healthLog.updatedAt || healthLog.updated_date,
            patient_id: patientId,
            patient_name: patientData?.patientName || 'Unknown Patient',
            status: getSugarProfileStatus(sugar),
            _original: sugar,
            _healthLog: healthLog
          };
          
          processedData.sugarProfile.push(sugarEntry);
        });
      }

      // **PROCESAR PERFIL HEPÁTICO**
      if (healthLog.liverProfile && Array.isArray(healthLog.liverProfile)) {
        healthLog.liverProfile.forEach((liver, index) => {
          const liverEntry = {
            liver_id: `${healthLog._id}_liver_${index}`,
            healthlog_id: healthLog._id,
            category: 'liverProfile',
            
            // Liver profile data
            alt: liver.alt || null,
            ast: liver.ast || null,
            alp: liver.alp || null,
            ggt: liver.ggt || null,
            bilirubin_total: liver.bilirubinTotal || null,
            albumin: liver.albumin || null,
            
            // Common fields
            created_date: healthLog.createdAt || healthLog.created_date,
            updated_date: healthLog.updatedAt || healthLog.updated_date,
            patient_id: patientId,
            patient_name: patientData?.patientName || 'Unknown Patient',
            status: getLiverProfileStatus(liver),
            _original: liver,
            _healthLog: healthLog
          };
          
          processedData.liverProfile.push(liverEntry);
        });
      }

      // **PROCESAR PERFIL RENAL**
      if (healthLog.kidneyProfile && Array.isArray(healthLog.kidneyProfile)) {
        healthLog.kidneyProfile.forEach((kidney, index) => {
          const kidneyEntry = {
            kidney_id: `${healthLog._id}_kidney_${index}`,
            healthlog_id: healthLog._id,
            category: 'kidneyProfile',
            
            // Kidney profile data
            creatinine: kidney.creatinine || null,
            urea: kidney.urea || null,
            egfr: kidney.egfr || null,
            sodium: kidney.sodium || null,
            potassium: kidney.potassium || null,
            
            // Common fields
            created_date: healthLog.createdAt || healthLog.created_date,
            updated_date: healthLog.updatedAt || healthLog.updated_date,
            patient_id: patientId,
            patient_name: patientData?.patientName || 'Unknown Patient',
            status: getKidneyProfileStatus(kidney),
            _original: kidney,
            _healthLog: healthLog
          };
          
          processedData.kidneyProfile.push(kidneyEntry);
        });
      }

      // **PROCESAR PERFIL LIPÍDICO**
      if (healthLog.lipidProfile && Array.isArray(healthLog.lipidProfile)) {
        healthLog.lipidProfile.forEach((lipid, index) => {
          const lipidEntry = {
            lipid_id: `${healthLog._id}_lipid_${index}`,
            healthlog_id: healthLog._id,
            category: 'lipidProfile',
            
            // Lipid profile data
            total_cholesterol: lipid.totalCholesterol || null,
            ldl: lipid.ldl || null,
            hdl: lipid.hdl || null,
            triglycerides: lipid.triglycerides || null,
            
            // Common fields
            created_date: healthLog.createdAt || healthLog.created_date,
            updated_date: healthLog.updatedAt || healthLog.updated_date,
            patient_id: patientId,
            patient_name: patientData?.patientName || 'Unknown Patient',
            status: getLipidProfileStatus(lipid),
            _original: lipid,
            _healthLog: healthLog
          };
          
          processedData.lipidProfile.push(lipidEntry);
        });
      }

      // **PROCESAR SEGUIMIENTO MENSTRUAL**
      if (healthLog.cycleTracking && Array.isArray(healthLog.cycleTracking)) {
        healthLog.cycleTracking.forEach((cycle, index) => {
          const cycleEntry = {
            cycle_id: `${healthLog._id}_cycle_${index}`,
            healthlog_id: healthLog._id,
            category: 'cycleTracking',
            
            // Cycle tracking data
            cycle_end_date: cycle.cycleEndDate || null,
            ovulation_date: cycle.ovulationDate || null,
            expected_next_period: cycle.expectedNextPeriod || null,
            cycle_length: cycle.cycleLength || null,
            flow_level: cycle.flowLevel || null,
            symptoms: cycle.symptoms || [],
            mood: cycle.mood || null,
            notes: cycle.notes || null,
            
            // Common fields
            created_date: healthLog.createdAt || healthLog.created_date,
            updated_date: healthLog.updatedAt || healthLog.updated_date,
            patient_id: patientId,
            patient_name: patientData?.patientName || 'Unknown Patient',
            status: getCycleTrackingStatus(cycle),
            _original: cycle,
            _healthLog: healthLog
          };
          
          processedData.cycleTracking.push(cycleEntry);
        });
      }

      // **PROCESAR SEGUIMIENTO DE EMBARAZO**
      if (healthLog.pregnancyTracking && Array.isArray(healthLog.pregnancyTracking)) {
        healthLog.pregnancyTracking.forEach((pregnancy, index) => {
          const pregnancyEntry = {
            pregnancy_id: `${healthLog._id}_pregnancy_${index}`,
            healthlog_id: healthLog._id,
            category: 'pregnancyTracking',
            
            // Pregnancy tracking data
            is_pregnant: pregnancy.isPregnant || false,
            gestational_age_weeks: pregnancy.gestationalAgeWeeks || null,
            fetal_heart_rate: pregnancy.fetalHeartRate || null,
            fundal_height: pregnancy.fundalHeight || null,
            weight: pregnancy.weight || null,
            bp_systolic: pregnancy.bloodPressure?.systolic || null,
            bp_diastolic: pregnancy.bloodPressure?.diastolic || null,
            glucose_challenge_test: pregnancy.glucoseChallengeTest || null,
            ultrasound_findings: pregnancy.ultrasoundFindings || null,
            symptoms: pregnancy.symptoms || [],
            medications: pregnancy.medications || [],
            doctor_notes: pregnancy.doctorNotes || null,
            next_appointment: pregnancy.nextAppointment || null,
            
            // Common fields
            created_date: healthLog.createdAt || healthLog.created_date,
            updated_date: healthLog.updatedAt || healthLog.updated_date,
            patient_id: patientId,
            patient_name: patientData?.patientName || 'Unknown Patient',
            status: getPregnancyTrackingStatus(pregnancy),
            _original: pregnancy,
            _healthLog: healthLog
          };
          
          processedData.pregnancyTracking.push(pregnancyEntry);
        });
      }
    });

    // Actualizar estados
    setAllHealthData(processedData);
    
    // Actualizar datos mostrados según categoría seleccionada
    const currentCategoryData = processedData[selectedCategory] || [];
    setHealthLogs(currentCategoryData);
    
    // Actualizar contador
    const totalRecords = Object.values(processedData).reduce((total, records) => total + records.length, 0);
    onCountUpdate && onCountUpdate(componentName, totalRecords);
    
  } catch (error) {
    console.error('Error loading health logs:', error);
    setHealthLogs([]);
    setAllHealthData({});
    onCountUpdate && onCountUpdate(componentName, 0);
  } finally {
    setIsLoading(false);
  }
}, [patientId, selectedCategory, onCountUpdate, componentName, patientData]);

useEffect(() => {
  console.log(`🔄 Categoría cambiada a: ${selectedCategory}`);
  console.log('AllHealthData disponible:', allHealthData);
  
  // Actualizar datos mostrados cuando cambie la categoría
  const currentCategoryData = allHealthData[selectedCategory] || [];
  console.log(`Datos para ${selectedCategory}:`, currentCategoryData);
  
  setHealthLogs(currentCategoryData);
}, [selectedCategory, allHealthData]);

const getBodyMeasurementStatus = (measurement) => {
  const hasData = measurement.height || measurement.weight || measurement.headCircumference;
  return hasData ? '1' : '0';
};

const getSugarProfileStatus = (sugar) => {
  const hasData = sugar.fastingBloodSugar || sugar.postPrandialSugar || sugar.hba1c;
  return hasData ? '1' : '0';
};

const getLiverProfileStatus = (liver) => {
  const hasData = liver.alt || liver.ast || liver.alp || liver.ggt || liver.bilirubinTotal || liver.albumin;
  return hasData ? '1' : '0';
};

const getKidneyProfileStatus = (kidney) => {
  const hasData = kidney.creatinine || kidney.urea || kidney.egfr || kidney.sodium || kidney.potassium;
  return hasData ? '1' : '0';
};

const getLipidProfileStatus = (lipid) => {
  const hasData = lipid.totalCholesterol || lipid.ldl || lipid.hdl || lipid.triglycerides;
  return hasData ? '1' : '0';
};

const getCycleTrackingStatus = (cycle) => {
  const hasData = cycle.cycleEndDate || cycle.ovulationDate || cycle.cycleLength;
  return hasData ? '1' : '0';
};

const getPregnancyTrackingStatus = (pregnancy) => {
  const hasData = pregnancy.isPregnant !== undefined || pregnancy.gestationalAgeWeeks || pregnancy.weight;
  return hasData ? '1' : '0';
};

// 4. AGREGAR FUNCIÓN PARA CAMBIAR CATEGORÍA
const handleCategoryChange = (category) => {
  console.log(`📋 Cambiando a categoría: ${category}`);
  setSelectedCategory(category);
  
  // Actualizar datos inmediatamente
  const categoryData = allHealthData[category] || [];
  console.log(`Datos encontrados para ${category}:`, categoryData.length);
  setHealthLogs(categoryData);
  
  // Limpiar filtros y selección
  setRowSelection({});
  setGlobalFilter('');
  setFilters({
    bloodPressureRange: 'All',
    heartRateRange: 'All',
    temperatureRange: 'All',
    dateFrom: '',
    dateTo: '',
    status: 'All'
  });
};

// 5. FUNCIÓN PARA OBTENER COLUMNAS SEGÚN CATEGORÍA
const getColumnsByCategory = (category) => {
  switch (category) {
    case 'vitals':
      return getVitalsColumns();
    case 'bodyMeasurements':
      return getBodyMeasurementsColumns();
    case 'sugarProfile':
      return getSugarProfileColumns();
    case 'liverProfile':
      return getLiverProfileColumns();
    case 'kidneyProfile':
      return getKidneyProfileColumns();
    case 'lipidProfile':
      return getLipidProfileColumns();
    case 'cycleTracking':
      return getCycleTrackingColumns();
    case 'pregnancyTracking':
      return getPregnancyTrackingColumns();
    default:
      return getVitalsColumns();
  }
};

const openForm = (category, editData = null) => {
  console.log('Opening form for category:', category, 'editData:', editData);
  setFormState({
    isOpen: true,
    category,
    editData,
    isSubmitting: false
  });
};

const closeForm = () => {
  console.log('Closing universal form');
  setFormState(prev => ({
    ...prev,
    isOpen: false,
    editData: null
  }));
};

const handleUniversalFormSubmit = async (data) => {
  console.log('=== SUBMITTING UNIVERSAL FORM ===');
  console.log('Category:', formState.category);
  console.log('Data:', data);
  console.log('Is Edit:', !!formState.editData);
  
  setFormState(prev => ({ ...prev, isSubmitting: true }));
  
  try {
    const currentUser = getCurrentUser();
    
    if (!currentUser || !currentUser.id) {
      throw new Error('No se pudo obtener información del usuario actual');
    }
    
    if (!patientId) {
      throw new Error('No se pudo obtener el ID del paciente');
    }

    // Get or create health log
    let existingHealthLog = await getOrCreateHealthLog(patientId);
    
    let result;
    
    if (formState.editData && formState.editData.editMode) {
      // EDIT EXISTING RECORD using specific function
      console.log('=== EDITING EXISTING RECORD ===');
      result = await handleUpdateRecord(formState.editData, formState.category, data);
    } else {
      // CREATE NEW RECORD using specific function
      console.log('=== CREATING NEW RECORD ===');
      result = await handleNewRecord(existingHealthLog, formState.category, data);
    }
    
    console.log('Universal form submitted successfully:', result);
    
    // Reload data
    await Promise.all([
      loadHealthLogs(),
      loadHealthLogStats()
    ]);
    
    // Close form
    closeForm();
    
    // Show success message
    const actionText = formState.editData?.editMode ? 'updated' : 'recorded';
    const categoryText = getCategoryDisplayName(formState.category);
    
    showMessage(`${categoryText} ${actionText} successfully!`, 'success');
    
  } catch (error) {
    console.error('Error in universal form submission:', error);
    
    const errorMessage = getErrorMessage(error);
    showMessage(`Error: ${errorMessage}`, 'error');
  } finally {
    setFormState(prev => ({ ...prev, isSubmitting: false }));
  }
};

// Helper function to get or create health log
const getOrCreateHealthLog = async (patientId) => {
  try {
    const response = await HealthLogService.getHealthLogsByUser(patientId);
    if (response.data && response.data.length > 0) {
      return response.data[0];
    }
  } catch (error) {
    console.log('No existing health log found, will create new one');
  }
  
  // Create new health log structure
  const newHealthLog = {
    userId: patientId,
    vitals: [],
    bodyMeasurements: [],
    sugarProfile: [],
    liverProfile: [],
    kidneyProfile: [],
    lipidProfile: [],
    cycleTracking: [],
    pregnancyTracking: []
  };
  
  const result = await HealthLogService.createHealthLog(newHealthLog);
  return result.data;
};

  // **FUNCIÓN SEPARADA PARA CARGAR ESTADÍSTICAS**
  const loadHealthLogStats = useCallback(async () => {
    if (!patientId || healthLogs.length === 0) return;
    
    try {
      // Check for abnormal vitals
      const abnormalVitals = healthLogs.filter(vital => {
        const hasHighBP = (vital.systolic > 140 || vital.diastolic > 90);
        const hasLowBP = (vital.systolic < 90 || vital.diastolic < 60);
        const hasAbnormalHR = (vital.heart_rate < 60 || vital.heart_rate > 100);
        const hasFever = (vital.temperature > 38.0);
        
        return hasHighBP || hasLowBP || hasAbnormalHR || hasFever;
      }).length;
      
      const recentVitals = healthLogs.filter(vital => {
        if (!vital.created_date) return false;
        const vitalDate = new Date(vital.created_date);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return vitalDate >= sevenDaysAgo;
      }).length;
      
      if (abnormalVitals > 0 || recentVitals > 0) {
        setAlertInfo({
          alert_type: 'vitals',
          message: `${healthLogs.length} vital records${abnormalVitals > 0 ? `, ${abnormalVitals} abnormal readings` : ''}`,
          priority: abnormalVitals > 0 ? 'high' : 'low',
          requires_action: abnormalVitals > 0
        });
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading health log stats:', error);
      setAlertInfo(null);
    }
  }, [patientId, healthLogs]);

  const applyFilters = useCallback(() => {
    console.log('🔄 Aplicando filtros y ordenamiento...');
    console.log('Health logs array length:', healthLogs.length);
    console.log('Current sortBy:', sortBy);
    
    let filtered = [...healthLogs];

    // Aplicar filtro de búsqueda global
    if (globalFilter) {
      filtered = filtered.filter(vital => 
        (vital.systolic?.toString() || '').includes(globalFilter) ||
        (vital.diastolic?.toString() || '').includes(globalFilter) ||
        (vital.heart_rate?.toString() || '').includes(globalFilter) ||
        (vital.temperature?.toString() || '').includes(globalFilter)
      );
    }

    // Aplicar filtros específicos
    if (filters.bloodPressureRange !== 'All') {
      filtered = filtered.filter(vital => {
        const systolic = vital.systolic;
        const diastolic = vital.diastolic;
        if (!systolic || !diastolic) return false;
        
        switch (filters.bloodPressureRange) {
          case 'Normal': return systolic < 120 && diastolic < 80;
          case 'Elevated': return systolic >= 120 && systolic < 130 && diastolic < 80;
          case 'High': return systolic >= 130 || diastolic >= 80;
          case 'Low': return systolic < 90 || diastolic < 60;
          default: return true;
        }
      });
    }

    if (filters.heartRateRange !== 'All') {
      filtered = filtered.filter(vital => {
        const hr = vital.heart_rate;
        if (!hr) return false;
        
        switch (filters.heartRateRange) {
          case 'Low': return hr < 60;
          case 'Normal': return hr >= 60 && hr <= 100;
          case 'High': return hr > 100;
          default: return true;
        }
      });
    }

    if (filters.temperatureRange !== 'All') {
      filtered = filtered.filter(vital => {
        const temp = vital.temperature;
        if (!temp) return false;
        
        switch (filters.temperatureRange) {
          case 'Low': return temp < 36.0;
          case 'Normal': return temp >= 36.0 && temp <= 37.5;
          case 'Fever': return temp > 37.5;
          default: return true;
        }
      });
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(vital => {
        const vitalDate = vital.created_date;
        return vitalDate && new Date(vitalDate) >= new Date(filters.dateFrom);
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter(vital => {
        const vitalDate = vital.created_date;
        return vitalDate && new Date(vitalDate) <= new Date(filters.dateTo);
      });
    }

    console.log('Filtered array length:', filtered.length);

    // Ordenamiento
    filtered.sort((a, b) => {
      console.log('Sorting with:', sortBy);
      
      const getDate = (item) => {
        const dateValue = item.created_date || new Date().toISOString();
        return new Date(dateValue);
      };
      
      switch (sortBy) {
        case 'newest':
          return getDate(b) - getDate(a);
        case 'oldest':
          return getDate(a) - getDate(b);
        case 'bp_high':
          return (b.systolic || 0) - (a.systolic || 0);
        case 'heart_rate':
          return (b.heart_rate || 0) - (a.heart_rate || 0);
        default:
          return 0;
      }
    });

    console.log('Final filtered array length:', filtered.length);
    setFilteredHealthLogs(filtered);
  }, [healthLogs, filters, globalFilter, sortBy]);

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleSortChange = useCallback((sortOption) => {
    console.log('Cambiando orden a:', sortOption);
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, []);

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'bp_high': return 'By Blood Pressure';
      case 'heart_rate': return 'By Heart Rate';
      default: return 'Sort by';
    }
  };
  // FUNCIÓN HELPER PARA BMI
const getBMICategory = (bmi) => {
  if (!bmi) return 'Not calculated';
  const bmiValue = parseFloat(bmi);
  
  if (bmiValue < 18.5) return 'Underweight';
  if (bmiValue < 25) return 'Normal';
  if (bmiValue < 30) return 'Overweight';
  return 'Obese';
};

  const getBodyMeasurementsColumns = () => [
  {
    accessorKey: 'height',
    header: 'Height',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.height ? `${row.original.height} cm` : 'Not recorded'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'weight',
    header: 'Weight',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.weight ? `${row.original.weight} kg` : 'Not recorded'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'bmi',
    header: 'BMI',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.bmi || 'N/A'}
        </div>
        <small className="text-muted">
          {getBMICategory(row.original.bmi)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'head_circumference',
    header: 'Head Circum.',
    size: 120,
    Cell: ({ row }) => (
      <div>
        {row.original.head_circumference ? `${row.original.head_circumference} cm` : 'N/A'}
      </div>
    )
  },
    {
      accessorKey: 'created_date',
      header: 'Date Recorded',
      size: 140,
      Cell: ({ row }) => (
        <div>
          <div>{formatDate(row.original.created_date)}</div>
          <small className="text-muted">
            {formatTime(row.original.created_date)}
          </small>
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      Cell: ({ row }) => (
        <span className={`badge bg-${getStatusColor(row.original.status)}`}>
          {getStatusText(row.original.status)}
        </span>
      )
    },
    {
      id: 'actions',
  header: 'Actions',
  size: 120,
  Cell: ({ row }) => (
    <div className="d-flex gap-1">
      <button
        className="btn btn-outline-primary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleViewRecord(row.original); // Función genérica
        }}
        title="View Details"
      >
        <FiEye />
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleEditRecord(row.original); // Función genérica
        }}
        title="Edit"
      >
        <FiEdit3 />
      </button>
      <button
        className="btn btn-outline-danger btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteRecord(row.original); // Función genérica (ya existe)
        }}
        title="Delete"
      >
        <FiTrash2 />
      </button>
    </div>
  )
    }
  ];


  // Table columns definition
  const getVitalsColumns = () => [
    {
      accessorKey: 'blood_pressure',
      header: 'Blood Pressure',
      size: 150,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div>
            <div className="fw-bold text-primary">
              {row.original.systolic && row.original.diastolic 
                ? `${row.original.systolic}/${row.original.diastolic}` 
                : 'Not recorded'}
            </div>
            <small className="text-muted">
              {getBPCategory(row.original.systolic, row.original.diastolic)}
            </small>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'heart_rate',
      header: 'Heart Rate',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <div className="fw-bold">
            {row.original.heart_rate ? `${row.original.heart_rate} bpm` : 'Not recorded'}
          </div>
          <small className="text-muted">
            {getHRCategory(row.original.heart_rate)}
          </small>
        </div>
      )
    },
    {
      accessorKey: 'temperature',
      header: 'Temperature',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <div className="fw-bold">
            {row.original.temperature ? `${row.original.temperature}°C` : 'Not recorded'}
          </div>
          <small className="text-muted">
            {getTempCategory(row.original.temperature)}
          </small>
        </div>
      )
    },
    {
      accessorKey: 'oxygen_saturation',
      header: 'O2 Sat',
      size: 100,
      Cell: ({ row }) => (
        <span className={`badge bg-${getOxygenSaturationColor(row.original.oxygen_saturation)}`}>
          {row.original.oxygen_saturation ? `${row.original.oxygen_saturation}%` : 'N/A'}
        </span>
      )
    },
    {
      accessorKey: 'respiratory_rate',
      header: 'Resp Rate',
      size: 100,
      Cell: ({ row }) => (
        <div>
          {row.original.respiratory_rate ? `${row.original.respiratory_rate}/min` : 'N/A'}
        </div>
      )
    },
    {
      accessorKey: 'created_date',
      header: 'Date Recorded',
      size: 140,
      Cell: ({ row }) => (
        <div>
          <div>{formatDate(row.original.created_date)}</div>
          <small className="text-muted">
            {formatTime(row.original.created_date)}
          </small>
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      Cell: ({ row }) => (
        <span className={`badge bg-${getStatusColor(row.original.status)}`}>
          {getStatusText(row.original.status)}
        </span>
      )
    },
    {
      id: 'actions',
  header: 'Actions',
  size: 120,
  Cell: ({ row }) => (
    <div className="d-flex gap-1">
      <button
        className="btn btn-outline-primary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleViewRecord(row.original); // Función genérica
        }}
        title="View Details"
      >
        <FiEye />
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleEditRecord(row.original); // Función genérica
        }}
        title="Edit"
      >
        <FiEdit3 />
      </button>
      <button
        className="btn btn-outline-danger btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteRecord(row.original); // Función genérica (ya existe)
        }}
        title="Delete"
      >
        <FiTrash2 />
      </button>
    </div>
  )
    }
  ];


// SUGAR PROFILE COLUMNS
const getSugarProfileColumns = () => [
  {
    accessorKey: 'fasting_blood_sugar',
    header: 'Fasting Sugar',
    size: 130,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.fasting_blood_sugar ? `${row.original.fasting_blood_sugar} mg/dL` : 'Not recorded'}
        </div>
        <small className="text-muted">
          {getSugarCategory(row.original.fasting_blood_sugar, 'fasting')}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'postprandial_sugar',
    header: 'Post-meal',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.postprandial_sugar ? `${row.original.postprandial_sugar} mg/dL` : 'Not recorded'}
        </div>
        <small className="text-muted">
          {getSugarCategory(row.original.postprandial_sugar, 'postprandial')}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'hba1c',
    header: 'HbA1c',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.hba1c ? `${row.original.hba1c}%` : 'N/A'}
        </div>
        <small className="text-muted">
          {getHbA1cCategory(row.original.hba1c)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'created_date',
    header: 'Date Recorded',
    size: 140,
    Cell: ({ row }) => (
      <div>
        <div>{formatDate(row.original.created_date)}</div>
        <small className="text-muted">
          {formatTime(row.original.created_date)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 100,
    Cell: ({ row }) => (
      <span className={`badge bg-${getStatusColor(row.original.status)}`}>
        {getStatusText(row.original.status)}
      </span>
    )
  },
    {
      id: 'actions',
  header: 'Actions',
  size: 120,
  Cell: ({ row }) => (
    <div className="d-flex gap-1">
      <button
        className="btn btn-outline-primary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleViewRecord(row.original); // Función genérica
        }}
        title="View Details"
      >
        <FiEye />
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleEditRecord(row.original); // Función genérica
        }}
        title="Edit"
      >
        <FiEdit3 />
      </button>
      <button
        className="btn btn-outline-danger btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteRecord(row.original); // Función genérica (ya existe)
        }}
        title="Delete"
      >
        <FiTrash2 />
      </button>
    </div>
  )
    }
  ];

// LIVER PROFILE COLUMNS
const getLiverProfileColumns = () => [
  {
    accessorKey: 'alt',
    header: 'ALT',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.alt ? `${row.original.alt} U/L` : 'N/A'}
        </div>
        <small className="text-muted">
          {getLiverEnzymeCategory(row.original.alt, 'ALT')}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'ast',
    header: 'AST',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.ast ? `${row.original.ast} U/L` : 'N/A'}
        </div>
        <small className="text-muted">
          {getLiverEnzymeCategory(row.original.ast, 'AST')}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'alp',
    header: 'ALP',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.alp ? `${row.original.alp} U/L` : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'bilirubin_total',
    header: 'Bilirubin',
    size: 110,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.bilirubin_total ? `${row.original.bilirubin_total} mg/dL` : 'N/A'}
        </div>
        <small className="text-muted">
          {getBilirubinCategory(row.original.bilirubin_total)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'albumin',
    header: 'Albumin',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.albumin ? `${row.original.albumin} g/dL` : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'created_date',
    header: 'Date Recorded',
    size: 140,
    Cell: ({ row }) => (
      <div>
        <div>{formatDate(row.original.created_date)}</div>
        <small className="text-muted">
          {formatTime(row.original.created_date)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 100,
    Cell: ({ row }) => (
      <span className={`badge bg-${getStatusColor(row.original.status)}`}>
        {getStatusText(row.original.status)}
      </span>
    )
  },
    {
      id: 'actions',
  header: 'Actions',
  size: 120,
  Cell: ({ row }) => (
    <div className="d-flex gap-1">
      <button
        className="btn btn-outline-primary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleViewRecord(row.original); // Función genérica
        }}
        title="View Details"
      >
        <FiEye />
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleEditRecord(row.original); // Función genérica
        }}
        title="Edit"
      >
        <FiEdit3 />
      </button>
      <button
        className="btn btn-outline-danger btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteRecord(row.original); // Función genérica (ya existe)
        }}
        title="Delete"
      >
        <FiTrash2 />
      </button>
    </div>
  )
    }
  ];

// KIDNEY PROFILE COLUMNS
const getKidneyProfileColumns = () => [
  {
    accessorKey: 'creatinine',
    header: 'Creatinine',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.creatinine ? `${row.original.creatinine} mg/dL` : 'N/A'}
        </div>
        <small className="text-muted">
          {getCreatinineCategory(row.original.creatinine)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'urea',
    header: 'Urea',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.urea ? `${row.original.urea} mg/dL` : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'egfr',
    header: 'eGFR',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.egfr ? `${row.original.egfr} mL/min` : 'N/A'}
        </div>
        <small className="text-muted">
          {getEGFRCategory(row.original.egfr)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'sodium',
    header: 'Sodium',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.sodium ? `${row.original.sodium} mEq/L` : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'potassium',
    header: 'Potassium',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.potassium ? `${row.original.potassium} mEq/L` : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'created_date',
    header: 'Date Recorded',
    size: 140,
    Cell: ({ row }) => (
      <div>
        <div>{formatDate(row.original.created_date)}</div>
        <small className="text-muted">
          {formatTime(row.original.created_date)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 100,
    Cell: ({ row }) => (
      <span className={`badge bg-${getStatusColor(row.original.status)}`}>
        {getStatusText(row.original.status)}
      </span>
    )
  },
    {
      id: 'actions',
  header: 'Actions',
  size: 120,
  Cell: ({ row }) => (
    <div className="d-flex gap-1">
      <button
        className="btn btn-outline-primary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleViewRecord(row.original); // Función genérica
        }}
        title="View Details"
      >
        <FiEye />
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleEditRecord(row.original); // Función genérica
        }}
        title="Edit"
      >
        <FiEdit3 />
      </button>
      <button
        className="btn btn-outline-danger btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteRecord(row.original); // Función genérica (ya existe)
        }}
        title="Delete"
      >
        <FiTrash2 />
      </button>
    </div>
  )
    }
  ];

// LIPID PROFILE COLUMNS
const getLipidProfileColumns = () => [
  {
    accessorKey: 'total_cholesterol',
    header: 'Total Chol.',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.total_cholesterol ? `${row.original.total_cholesterol} mg/dL` : 'N/A'}
        </div>
        <small className="text-muted">
          {getCholesterolCategory(row.original.total_cholesterol)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'ldl',
    header: 'LDL',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.ldl ? `${row.original.ldl} mg/dL` : 'N/A'}
        </div>
        <small className="text-muted">
          {getLDLCategory(row.original.ldl)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'hdl',
    header: 'HDL',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.hdl ? `${row.original.hdl} mg/dL` : 'N/A'}
        </div>
        <small className="text-muted">
          {getHDLCategory(row.original.hdl)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'triglycerides',
    header: 'Triglycerides',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.triglycerides ? `${row.original.triglycerides} mg/dL` : 'N/A'}
        </div>
        <small className="text-muted">
          {getTriglyceridesCategory(row.original.triglycerides)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'created_date',
    header: 'Date Recorded',
    size: 140,
    Cell: ({ row }) => (
      <div>
        <div>{formatDate(row.original.created_date)}</div>
        <small className="text-muted">
          {formatTime(row.original.created_date)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 100,
    Cell: ({ row }) => (
      <span className={`badge bg-${getStatusColor(row.original.status)}`}>
        {getStatusText(row.original.status)}
      </span>
    )
  },
    {
      id: 'actions',
  header: 'Actions',
  size: 120,
  Cell: ({ row }) => (
    <div className="d-flex gap-1">
      <button
        className="btn btn-outline-primary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleViewRecord(row.original); // Función genérica
        }}
        title="View Details"
      >
        <FiEye />
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleEditRecord(row.original); // Función genérica
        }}
        title="Edit"
      >
        <FiEdit3 />
      </button>
      <button
        className="btn btn-outline-danger btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteRecord(row.original); // Función genérica (ya existe)
        }}
        title="Delete"
      >
        <FiTrash2 />
      </button>
    </div>
  )
    }
  ];

// CYCLE TRACKING COLUMNS
const getCycleTrackingColumns = () => [
  {
    accessorKey: 'cycle_length',
    header: 'Cycle Length',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.cycle_length ? `${row.original.cycle_length} days` : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'flow_level',
    header: 'Flow Level',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <span className={`badge bg-${getFlowLevelColor(row.original.flow_level)}`}>
          {row.original.flow_level || 'N/A'}
        </span>
      </div>
    )
  },
  {
    accessorKey: 'ovulation_date',
    header: 'Ovulation',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div>{formatDate(row.original.ovulation_date)}</div>
      </div>
    )
  },
  {
    accessorKey: 'expected_next_period',
    header: 'Next Period',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div>{formatDate(row.original.expected_next_period)}</div>
      </div>
    )
  },
  {
    accessorKey: 'symptoms',
    header: 'Symptoms',
    size: 150,
    Cell: ({ row }) => (
      <div>
        {row.original.symptoms && row.original.symptoms.length > 0 ? (
          <small className="text-muted">
            {row.original.symptoms.slice(0, 2).join(', ')}
            {row.original.symptoms.length > 2 && ` +${row.original.symptoms.length - 2} more`}
          </small>
        ) : (
          <small className="text-muted">None reported</small>
        )}
      </div>
    )
  },
  {
    accessorKey: 'created_date',
    header: 'Date Recorded',
    size: 140,
    Cell: ({ row }) => (
      <div>
        <div>{formatDate(row.original.created_date)}</div>
        <small className="text-muted">
          {formatTime(row.original.created_date)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 100,
    Cell: ({ row }) => (
      <span className={`badge bg-${getStatusColor(row.original.status)}`}>
        {getStatusText(row.original.status)}
      </span>
    )
  },
    {
      id: 'actions',
  header: 'Actions',
  size: 120,
  Cell: ({ row }) => (
    <div className="d-flex gap-1">
      <button
        className="btn btn-outline-primary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleViewRecord(row.original); // Función genérica
        }}
        title="View Details"
      >
        <FiEye />
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleEditRecord(row.original); // Función genérica
        }}
        title="Edit"
      >
        <FiEdit3 />
      </button>
      <button
        className="btn btn-outline-danger btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteRecord(row.original); // Función genérica (ya existe)
        }}
        title="Delete"
      >
        <FiTrash2 />
      </button>
    </div>
  )
    }
  ];

// PREGNANCY TRACKING COLUMNS
const getPregnancyTrackingColumns = () => [
  {
    accessorKey: 'gestational_age_weeks',
    header: 'Gestation',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.gestational_age_weeks ? `${row.original.gestational_age_weeks} weeks` : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'fetal_heart_rate',
    header: 'Fetal HR',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.fetal_heart_rate ? `${row.original.fetal_heart_rate} bpm` : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'weight',
    header: 'Weight',
    size: 100,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.weight ? `${row.original.weight} kg` : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'bp_systolic',
    header: 'Blood Pressure',
    size: 130,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.bp_systolic && row.original.bp_diastolic 
            ? `${row.original.bp_systolic}/${row.original.bp_diastolic}` 
            : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'fundal_height',
    header: 'Fundal Height',
    size: 110,
    Cell: ({ row }) => (
      <div>
        <div className="fw-bold">
          {row.original.fundal_height ? `${row.original.fundal_height} cm` : 'N/A'}
        </div>
      </div>
    )
  },
  {
    accessorKey: 'next_appointment',
    header: 'Next Appt.',
    size: 120,
    Cell: ({ row }) => (
      <div>
        <div>{formatDate(row.original.next_appointment)}</div>
      </div>
    )
  },
  {
    accessorKey: 'created_date',
    header: 'Date Recorded',
    size: 140,
    Cell: ({ row }) => (
      <div>
        <div>{formatDate(row.original.created_date)}</div>
        <small className="text-muted">
          {formatTime(row.original.created_date)}
        </small>
      </div>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 100,
    Cell: ({ row }) => (
      <span className={`badge bg-${getStatusColor(row.original.status)}`}>
        {getStatusText(row.original.status)}
      </span>
    )
  },
    {
      id: 'actions',
  header: 'Actions',
  size: 120,
  Cell: ({ row }) => (
    <div className="d-flex gap-1">
      <button
        className="btn btn-outline-primary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleViewRecord(row.original); // Función genérica
        }}
        title="View Details"
      >
        <FiEye />
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleEditRecord(row.original); // Función genérica
        }}
        title="Edit"
      >
        <FiEdit3 />
      </button>
      <button
        className="btn btn-outline-danger btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteRecord(row.original); // Función genérica (ya existe)
        }}
        title="Delete"
      >
        <FiTrash2 />
      </button>
    </div>
  )
    }
  ];

// ========================================================================
// FUNCIONES HELPER PARA CATEGORIZACIÓN DE VALORES
// ========================================================================

// Sugar Profile Categories
const getSugarCategory = (value, type) => {
  if (!value) return 'Not recorded';
  
  if (type === 'fasting') {
    if (value < 70) return 'Low';
    if (value < 100) return 'Normal';
    if (value < 126) return 'Prediabetic';
    return 'Diabetic';
  } else if (type === 'postprandial') {
    if (value < 140) return 'Normal';
    if (value < 200) return 'Impaired';
    return 'Diabetic';
  }
  
  return 'Check range';
};

const getHbA1cCategory = (value) => {
  if (!value) return 'Not recorded';
  
  if (value < 5.7) return 'Normal';
  if (value < 6.5) return 'Prediabetic';
  return 'Diabetic';
};

// Liver Profile Categories
const getLiverEnzymeCategory = (value, enzyme) => {
  if (!value) return 'Not recorded';
  
  const normalRanges = {
    'ALT': { min: 7, max: 56 },
    'AST': { min: 10, max: 40 }
  };
  
  const range = normalRanges[enzyme];
  if (!range) return 'Check range';
  
  if (value < range.min) return 'Low';
  if (value <= range.max) return 'Normal';
  return 'Elevated';
};

const getBilirubinCategory = (value) => {
  if (!value) return 'Not recorded';
  
  if (value <= 1.2) return 'Normal';
  return 'Elevated';
};

// Kidney Profile Categories
const getCreatinineCategory = (value) => {
  if (!value) return 'Not recorded';
  
  if (value <= 1.2) return 'Normal';
  return 'Elevated';
};

const getEGFRCategory = (value) => {
  if (!value) return 'Not recorded';
  
  if (value >= 90) return 'Normal';
  if (value >= 60) return 'Mild decrease';
  if (value >= 30) return 'Moderate decrease';
  return 'Severe decrease';
};

// Lipid Profile Categories
const getCholesterolCategory = (value) => {
  if (!value) return 'Not recorded';
  
  if (value < 200) return 'Desirable';
  if (value < 240) return 'Borderline';
  return 'High';
};

const getLDLCategory = (value) => {
  if (!value) return 'Not recorded';
  
  if (value < 100) return 'Optimal';
  if (value < 130) return 'Near optimal';
  if (value < 160) return 'Borderline';
  return 'High';
};

const getHDLCategory = (value) => {
  if (!value) return 'Not recorded';
  
  if (value < 40) return 'Low';
  if (value >= 60) return 'High (good)';
  return 'Normal';
};

const getTriglyceridesCategory = (value) => {
  if (!value) return 'Not recorded';
  
  if (value < 150) return 'Normal';
  if (value < 200) return 'Borderline';
  if (value < 500) return 'High';
  return 'Very high';
};

// Cycle Tracking Categories
const getFlowLevelColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'light': return 'info';
    case 'moderate': return 'primary';
    case 'heavy': return 'warning';
    default: return 'secondary';
  }
};

  // **EFECTO PRINCIPAL PARA CARGAR DATOS**
  useEffect(() => {
    console.log(`🔄 ${componentName}: useEffect triggered, patientId:`, patientId);
    if (patientId) {
      loadHealthLogs();
    }
  }, [patientId, loadHealthLogs]);

  // **EFECTO SEPARADO PARA ESTADÍSTICAS**
  useEffect(() => {
    if (healthLogs.length > 0) {
      loadHealthLogStats();
    }
  }, [healthLogs, loadHealthLogStats]);

  // **EFECTO PARA ACTUALIZAR CONTADOR CUANDO CAMBIEN LOS DATOS**
  useEffect(() => {
    console.log(`📊 ${componentName}: Actualizando contador a ${healthLogs.length}`);
    onCountUpdate && onCountUpdate(componentName, healthLogs.length);
  }, [healthLogs.length, onCountUpdate, componentName]);

  // Apply filters whenever healthLogs or filters change
  useEffect(() => {
    console.log('📊 useEffect applyFilters triggered');
    console.log('- healthLogs.length:', healthLogs.length);
    console.log('- sortBy:', sortBy);
    
    if (healthLogs.length > 0) {
      applyFilters();
    } else {
      console.log('⚠️ No hay health logs, limpiando filteredHealthLogs');
      setFilteredHealthLogs([]);
    }
  }, [healthLogs, filters, globalFilter, sortBy, applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
  if (showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showEditOffcanvas || formState.isOpen) {
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '17px';
  } else {
    document.body.style.overflow = 'unset';
    document.body.style.paddingRight = '0px';
  }
  
  return () => {
    document.body.style.overflow = 'unset';
    document.body.style.paddingRight = '0px';
  };
}, [showFilterOffcanvas, showAddOffcanvas, showViewOffcanvas, showEditOffcanvas, formState.isOpen]);


  // Solo mostrar loading si estamos esperando datos específicos
  if (isLoading && patientId) {
    return <div>Loading patient data...</div>;
  }

  

const handleViewRecord = (record) => {
  console.log('Viewing record:', record);
  setSelectedHealthLog(record);
  setShowViewOffcanvas(true);
};

const handleEditRecord = async (record) => {
  console.log('=== PREPARING RECORD FOR EDIT ===');
  console.log('Record:', record);
  console.log('Selected Category:', selectedCategory);
  
  try {
    const healthLogId = record.healthlog_id;
    if (!healthLogId) {
      throw new Error('Health log ID not found');
    }
    
    // Obtener el health log completo
    const response = await HealthLogService.getHealthLogById(healthLogId);
    const healthLog = response.data;
    
    if (!healthLog) {
      throw new Error('Health log not found');
    }

    console.log('📋 Complete HealthLog:', healthLog);

    let recordIndex = -1;
    let recordData = null;
    
    // Determinar índice y datos específicos según categoría
    switch (selectedCategory) {
      case 'vitals':
        recordIndex = parseInt(record.vital_id.split('_vital_')[1]);
        recordData = healthLog.vitals?.[recordIndex];
        console.log('💓 Vitals array:', healthLog.vitals);
        console.log('💓 Record index:', recordIndex);
        console.log('💓 Found vital data:', recordData);
        console.log('💓 Blood pressure data:', recordData?.bloodPressure);
        
        // AÑADIR: Debug específico para blood pressure
        if (recordData) {
          debugBloodPressureData(recordData, record);
        }
        break;
        
      case 'bodyMeasurements':
        recordIndex = parseInt(record.measurement_id.split('_measurement_')[1]);
        recordData = healthLog.bodyMeasurements?.[recordIndex];
        break;
        
      case 'sugarProfile':
        recordIndex = parseInt(record.sugar_id.split('_sugar_')[1]);
        recordData = healthLog.sugarProfile?.[recordIndex];
        break;
        
      case 'liverProfile':
        recordIndex = parseInt(record.liver_id.split('_liver_')[1]);
        recordData = healthLog.liverProfile?.[recordIndex];
        break;
        
      case 'kidneyProfile':
        recordIndex = parseInt(record.kidney_id.split('_kidney_')[1]);
        recordData = healthLog.kidneyProfile?.[recordIndex];
        break;
        
      case 'lipidProfile':
        recordIndex = parseInt(record.lipid_id.split('_lipid_')[1]);
        recordData = healthLog.lipidProfile?.[recordIndex];
        break;
        
      case 'cycleTracking':
        recordIndex = parseInt(record.cycle_id.split('_cycle_')[1]);
        recordData = healthLog.cycleTracking?.[recordIndex];
        break;
        
      case 'pregnancyTracking':
        recordIndex = parseInt(record.pregnancy_id.split('_pregnancy_')[1]);
        recordData = healthLog.pregnancyTracking?.[recordIndex];
        break;
        
      default:
        throw new Error(`Unknown category: ${selectedCategory}`);
    }
    
    // Verificar que el registro existe
    if (!recordData) {
      throw new Error(`Record not found at index ${recordIndex} in ${selectedCategory}`);
    }
    
    console.log('✅ Found record data for editing:', recordData);
    console.log('📍 Record index:', recordIndex);
    
    // Obtener datos procesados específicos para la categoría
    const processedEditData = getProcessedEditData(selectedCategory, recordData, record);
    console.log('🔄 Processed edit data:', processedEditData);
    
    // AÑADIR: Verificación adicional para vitales
    if (selectedCategory === 'vitals') {
      console.log('🔍 FINAL VITAL EDIT DATA CHECK:');
      console.log('Systolic value:', processedEditData.systolic);
      console.log('Diastolic value:', processedEditData.diastolic);
      console.log('Heart Rate value:', processedEditData.heartRate);
      console.log('Temperature value:', processedEditData.temperature);
    }
    
    // Cerrar otros offcanvas que puedan estar abiertos
    setShowFilterOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowAddOffcanvas(false);
    
    // Abrir el formulario universal para editar con datos específicos
    const editDataForForm = {
      editMode: true,
      recordIndex: recordIndex,
      recordData: recordData,
      healthLogId: healthLogId,
      originalHealthLog: healthLog,
      // Agregar datos procesados según la categoría
      ...processedEditData
    };
    
    console.log('📝 Final edit data for form:', editDataForForm);
    
    openForm(selectedCategory, editDataForForm);
    
  } catch (error) {
    console.error('❌ Error preparing record for edit:', error);
    
    const errorMessage = getErrorMessage(error);
    showMessage(`Error loading record for edit: ${errorMessage}`, 'error');
  }
};

const debugBloodPressureData = (recordData, displayRecord) => {
  console.log('🩺 DEBUGGING BLOOD PRESSURE DATA:');
  console.log('Original recordData structure:', {
    bloodPressure: recordData.bloodPressure,
    directSystolic: recordData.systolic,
    directDiastolic: recordData.diastolic,
    fullRecord: recordData
  });
  
  console.log('Display record structure:', {
    systolic: displayRecord.systolic,
    diastolic: displayRecord.diastolic,
    fullDisplayRecord: displayRecord
  });
  
  // Determinar el mejor valor para systolic
  const bestSystolic = recordData.bloodPressure?.systolic || 
                      displayRecord.systolic || 
                      recordData.systolic || 
                      null;
                      
  // Determinar el mejor valor para diastolic
  const bestDiastolic = recordData.bloodPressure?.diastolic || 
                       displayRecord.diastolic || 
                       recordData.diastolic || 
                       null;
  
  console.log('Best values determined:', {
    systolic: bestSystolic,
    diastolic: bestDiastolic
  });
  
  return { bestSystolic, bestDiastolic };
};

// ========================================================================
// FUNCIÓN HELPER PARA PROCESAR DATOS DE EDICIÓN SEGÚN CATEGORÍA (CORREGIDA)
// ========================================================================

const getProcessedEditData = (category, recordData, displayRecord) => {
  console.log('🔧 getProcessedEditData called with:');
  console.log('Category:', category);
  console.log('RecordData:', recordData);
  console.log('DisplayRecord:', displayRecord);
  
  switch (category) {
    case 'vitals':
      // CORREGIDO: Mapear correctamente los datos de blood pressure
      const processedVitals = {
        // Para Blood Pressure, priorizar la estructura original
        systolic: recordData.bloodPressure?.systolic || 
                 displayRecord.systolic || 
                 recordData.systolic || 
                 null,
        diastolic: recordData.bloodPressure?.diastolic || 
                  displayRecord.diastolic || 
                  recordData.diastolic || 
                  null,
        
        // Para otros campos, usar el mapeo directo
        heartRate: recordData.heartRate || 
                  displayRecord.heart_rate || 
                  null,
        temperature: recordData.temperature || 
                    displayRecord.temperature || 
                    null,
        oxygenSaturation: recordData.oxygenSaturation || 
                         displayRecord.oxygen_saturation || 
                         null,
        respiratoryRate: recordData.respiratoryRate || 
                        displayRecord.respiratory_rate || 
                        null
      };
      
      console.log('📊 Processed vitals data:', processedVitals);
      console.log('📊 Blood pressure extracted:', {
        systolic: processedVitals.systolic,
        diastolic: processedVitals.diastolic,
        source_bp: recordData.bloodPressure,
        source_display: { systolic: displayRecord.systolic, diastolic: displayRecord.diastolic }
      });
      
      return processedVitals;
      
    case 'bodyMeasurements':
      return {
        height: recordData.height || displayRecord.height,
        weight: recordData.weight || displayRecord.weight,
        headCircumference: recordData.headCircumference || displayRecord.head_circumference
      };
      
    case 'sugarProfile':
      return {
        fastingBloodSugar: recordData.fastingBloodSugar || displayRecord.fasting_blood_sugar,
        postPrandialSugar: recordData.postPrandialSugar || displayRecord.postprandial_sugar,
        hba1c: recordData.hba1c || displayRecord.hba1c
      };
      
    case 'liverProfile':
      return {
        alt: recordData.alt || displayRecord.alt,
        ast: recordData.ast || displayRecord.ast,
        alp: recordData.alp || displayRecord.alp,
        ggt: recordData.ggt || displayRecord.ggt,
        bilirubinTotal: recordData.bilirubinTotal || displayRecord.bilirubin_total,
        albumin: recordData.albumin || displayRecord.albumin
      };
      
    case 'kidneyProfile':
      return {
        creatinine: recordData.creatinine || displayRecord.creatinine,
        urea: recordData.urea || displayRecord.urea,
        egfr: recordData.egfr || displayRecord.egfr,
        sodium: recordData.sodium || displayRecord.sodium,
        potassium: recordData.potassium || displayRecord.potassium
      };
      
    case 'lipidProfile':
      return {
        totalCholesterol: recordData.totalCholesterol || displayRecord.total_cholesterol,
        ldl: recordData.ldl || displayRecord.ldl,
        hdl: recordData.hdl || displayRecord.hdl,
        triglycerides: recordData.triglycerides || displayRecord.triglycerides
      };
      
    case 'cycleTracking':
      return {
        cycleEndDate: recordData.cycleEndDate || displayRecord.cycle_end_date,
        ovulationDate: recordData.ovulationDate || displayRecord.ovulation_date,
        expectedNextPeriod: recordData.expectedNextPeriod || displayRecord.expected_next_period,
        cycleLength: recordData.cycleLength || displayRecord.cycle_length,
        flowLevel: recordData.flowLevel || displayRecord.flow_level,
        symptoms: recordData.symptoms || displayRecord.symptoms || [],
        mood: recordData.mood || displayRecord.mood,
        notes: recordData.notes || displayRecord.notes
      };
      
    case 'pregnancyTracking':
      return {
        isPregnant: recordData.isPregnant !== undefined ? recordData.isPregnant : displayRecord.is_pregnant,
        gestationalAgeWeeks: recordData.gestationalAgeWeeks || displayRecord.gestational_age_weeks,
        fetalHeartRate: recordData.fetalHeartRate || displayRecord.fetal_heart_rate,
        fundalHeight: recordData.fundalHeight || displayRecord.fundal_height,
        weight: recordData.weight || displayRecord.weight,
        // CORREGIDO: Mapear correctamente BP para embarazo también
        bpSystolic: recordData.bloodPressure?.systolic || 
                   displayRecord.bp_systolic || 
                   recordData.bpSystolic || 
                   null,
        bpDiastolic: recordData.bloodPressure?.diastolic || 
                    displayRecord.bp_diastolic || 
                    recordData.bpDiastolic || 
                    null,
        glucoseChallengeTest: recordData.glucoseChallengeTest || displayRecord.glucose_challenge_test,
        ultrasoundFindings: recordData.ultrasoundFindings || displayRecord.ultrasound_findings,
        symptoms: recordData.symptoms || displayRecord.symptoms || [],
        medications: recordData.medications || displayRecord.medications || [],
        doctorNotes: recordData.doctorNotes || displayRecord.doctor_notes,
        nextAppointment: recordData.nextAppointment || displayRecord.next_appointment
      };
      
    default:
      return {};
  }
};


const handleUpdateRecord = async (editData, category, data) => {
  console.log(`=== UPDATING ${category.toUpperCase()} RECORD ===`);
  console.log('Edit Data:', editData);
  console.log('New Data:', data);
  
  const { healthLogId, recordIndex, originalHealthLog } = editData;
  
  if (!healthLogId || recordIndex === undefined || !originalHealthLog) {
    throw new Error('Missing required edit data');
  }
  
  const updatedHealthLog = { ...originalHealthLog };
  
  // Verificar que el array y el índice existen
  if (!updatedHealthLog[category] || !updatedHealthLog[category][recordIndex]) {
    throw new Error(`Record not found at index ${recordIndex} in ${category}`);
  }
  
  let updatedEntry;
  
  switch (category) {
    case 'vitals':
      updatedEntry = {
        ...updatedHealthLog.vitals[recordIndex], // Mantener datos existentes
        timestamp: updatedHealthLog.vitals[recordIndex].timestamp || new Date(),
        bloodPressure: {
          systolic: data.systolic || null,
          diastolic: data.diastolic || null
        },
        heartRate: data.heartRate || null,
        temperature: data.temperature || null,
        oxygenSaturation: data.oxygenSaturation || null,
        respiratoryRate: data.respiratoryRate || null
      };
      updatedHealthLog.vitals[recordIndex] = updatedEntry;
      break;
      
    case 'bodyMeasurements':
      updatedEntry = {
        ...updatedHealthLog.bodyMeasurements[recordIndex],
        timestamp: updatedHealthLog.bodyMeasurements[recordIndex].timestamp || new Date(),
        height: data.height || null,
        weight: data.weight || null,
        headCircumference: data.headCircumference || null,
        bmi: (data.height && data.weight) ? 
          parseFloat((data.weight / Math.pow(data.height/100, 2)).toFixed(1)) : null
      };
      updatedHealthLog.bodyMeasurements[recordIndex] = updatedEntry;
      break;
      
    case 'sugarProfile':
      updatedEntry = {
        ...updatedHealthLog.sugarProfile[recordIndex],
        timestamp: updatedHealthLog.sugarProfile[recordIndex].timestamp || new Date(),
        fastingBloodSugar: data.fastingBloodSugar || null,
        postPrandialSugar: data.postPrandialSugar || null,
        hba1c: data.hba1c || null
      };
      updatedHealthLog.sugarProfile[recordIndex] = updatedEntry;
      break;
      
    case 'liverProfile':
      updatedEntry = {
        ...updatedHealthLog.liverProfile[recordIndex],
        timestamp: updatedHealthLog.liverProfile[recordIndex].timestamp || new Date(),
        alt: data.alt || null,
        ast: data.ast || null,
        alp: data.alp || null,
        ggt: data.ggt || null,
        bilirubinTotal: data.bilirubinTotal || null,
        albumin: data.albumin || null
      };
      updatedHealthLog.liverProfile[recordIndex] = updatedEntry;
      break;
      
    case 'kidneyProfile':
      updatedEntry = {
        ...updatedHealthLog.kidneyProfile[recordIndex],
        timestamp: updatedHealthLog.kidneyProfile[recordIndex].timestamp || new Date(),
        creatinine: data.creatinine || null,
        urea: data.urea || null,
        egfr: data.egfr || null,
        sodium: data.sodium || null,
        potassium: data.potassium || null
      };
      updatedHealthLog.kidneyProfile[recordIndex] = updatedEntry;
      break;
      
    case 'lipidProfile':
      updatedEntry = {
        ...updatedHealthLog.lipidProfile[recordIndex],
        timestamp: updatedHealthLog.lipidProfile[recordIndex].timestamp || new Date(),
        totalCholesterol: data.totalCholesterol || null,
        ldl: data.ldl || null,
        hdl: data.hdl || null,
        triglycerides: data.triglycerides || null,
        cholesterolRatio: (data.totalCholesterol && data.hdl) ? 
          parseFloat((data.totalCholesterol / data.hdl).toFixed(1)) : null
      };
      updatedHealthLog.lipidProfile[recordIndex] = updatedEntry;
      break;
      
    case 'cycleTracking':
      updatedEntry = {
        ...updatedHealthLog.cycleTracking[recordIndex],
        timestamp: updatedHealthLog.cycleTracking[recordIndex].timestamp || new Date(),
        cycleEndDate: data.cycleEndDate || null,
        ovulationDate: data.ovulationDate || null,
        expectedNextPeriod: data.expectedNextPeriod || null,
        cycleLength: data.cycleLength || null,
        flowLevel: data.flowLevel || null,
        symptoms: data.symptoms || [],
        mood: data.mood || null,
        notes: data.notes || null
      };
      updatedHealthLog.cycleTracking[recordIndex] = updatedEntry;
      break;
      
    case 'pregnancyTracking':
      updatedEntry = {
        ...updatedHealthLog.pregnancyTracking[recordIndex],
        timestamp: updatedHealthLog.pregnancyTracking[recordIndex].timestamp || new Date(),
        isPregnant: data.isPregnant || false,
        gestationalAgeWeeks: data.gestationalAgeWeeks || null,
        fetalHeartRate: data.fetalHeartRate || null,
        fundalHeight: data.fundalHeight || null,
        weight: data.weight || null,
        bloodPressure: {
          systolic: data.bpSystolic || null,
          diastolic: data.bpDiastolic || null
        },
        glucoseChallengeTest: data.glucoseChallengeTest || null,
        ultrasoundFindings: data.ultrasoundFindings || null,
        symptoms: data.symptoms || [],
        medications: data.medications || [],
        doctorNotes: data.doctorNotes || null,
        nextAppointment: data.nextAppointment || null
      };
      updatedHealthLog.pregnancyTracking[recordIndex] = updatedEntry;
      break;
      
    default:
      throw new Error(`Unknown category for update: ${category}`);
  }
  
  console.log('Updated entry:', updatedEntry);
  console.log('Updating health log with ID:', healthLogId);
  
  return await HealthLogService.updateHealthLog(healthLogId, updatedHealthLog);
};

// Handle creating new records
const handleNewRecord = async (healthLog, category, data) => {
  const updatedHealthLog = { ...healthLog };
  
  switch (category) {
    case 'vitals':
      updatedHealthLog.vitals = updatedHealthLog.vitals || [];
      updatedHealthLog.vitals.push(createVitalEntry(data));
      break;
      
    case 'bodyMeasurements':
      updatedHealthLog.bodyMeasurements = updatedHealthLog.bodyMeasurements || [];
      updatedHealthLog.bodyMeasurements.push(createBodyMeasurementEntry(data));
      break;
      
    case 'sugarProfile':
      updatedHealthLog.sugarProfile = updatedHealthLog.sugarProfile || [];
      updatedHealthLog.sugarProfile.push(createSugarProfileEntry(data));
      break;
      
    case 'liverProfile':
      updatedHealthLog.liverProfile = updatedHealthLog.liverProfile || [];
      updatedHealthLog.liverProfile.push(createLiverProfileEntry(data));
      break;
      
    case 'kidneyProfile':
      updatedHealthLog.kidneyProfile = updatedHealthLog.kidneyProfile || [];
      updatedHealthLog.kidneyProfile.push(createKidneyProfileEntry(data));
      break;
      
    case 'lipidProfile':
      updatedHealthLog.lipidProfile = updatedHealthLog.lipidProfile || [];
      updatedHealthLog.lipidProfile.push(createLipidProfileEntry(data));
      break;
      
    case 'cycleTracking':
      updatedHealthLog.cycleTracking = updatedHealthLog.cycleTracking || [];
      updatedHealthLog.cycleTracking.push(createCycleTrackingEntry(data));
      break;
      
    case 'pregnancyTracking':
      updatedHealthLog.pregnancyTracking = updatedHealthLog.pregnancyTracking || [];
      updatedHealthLog.pregnancyTracking.push(createPregnancyTrackingEntry(data));
      break;
      
    default:
      throw new Error(`Unknown category for creation: ${category}`);
  }
  
  return await HealthLogService.updateHealthLog(healthLog._id, updatedHealthLog);
};

// Entry creation functions
const createVitalEntry = (data) => ({
  timestamp: new Date(),
  bloodPressure: {
    systolic: data.systolic || null,
    diastolic: data.diastolic || null
  },
  heartRate: data.heartRate || null,
  temperature: data.temperature || null,
  oxygenSaturation: data.oxygenSaturation || null,
  respiratoryRate: data.respiratoryRate || null
});

const createBodyMeasurementEntry = (data) => ({
  timestamp: new Date(),
  height: data.height || null,
  weight: data.weight || null,
  headCircumference: data.headCircumference || null,
  bmi: (data.height && data.weight) ? 
    parseFloat((data.weight / Math.pow(data.height/100, 2)).toFixed(1)) : null
});

const createSugarProfileEntry = (data) => ({
  timestamp: new Date(),
  fastingBloodSugar: data.fastingBloodSugar || null,
  postPrandialSugar: data.postPrandialSugar || null,
  hba1c: data.hba1c || null
});

const createLiverProfileEntry = (data) => ({
  timestamp: new Date(),
  alt: data.alt || null,
  ast: data.ast || null,
  alp: data.alp || null,
  ggt: data.ggt || null,
  bilirubinTotal: data.bilirubinTotal || null,
  albumin: data.albumin || null
});

const createKidneyProfileEntry = (data) => ({
  timestamp: new Date(),
  creatinine: data.creatinine || null,
  urea: data.urea || null,
  egfr: data.egfr || null,
  sodium: data.sodium || null,
  potassium: data.potassium || null
});

const createLipidProfileEntry = (data) => ({
  timestamp: new Date(),
  totalCholesterol: data.totalCholesterol || null,
  ldl: data.ldl || null,
  hdl: data.hdl || null,
  triglycerides: data.triglycerides || null,
  cholesterolRatio: (data.totalCholesterol && data.hdl) ? 
    parseFloat((data.totalCholesterol / data.hdl).toFixed(1)) : null
});

const createCycleTrackingEntry = (data) => ({
  timestamp: new Date(),
  cycleEndDate: data.cycleEndDate || null,
  ovulationDate: data.ovulationDate || null,
  expectedNextPeriod: data.expectedNextPeriod || null,
  cycleLength: data.cycleLength || null,
  flowLevel: data.flowLevel || null,
  symptoms: data.symptoms || [],
  mood: data.mood || null,
  notes: data.notes || null
});

const createPregnancyTrackingEntry = (data) => ({
  timestamp: new Date(),
  isPregnant: data.isPregnant || false,
  gestationalAgeWeeks: data.gestationalAgeWeeks || null,
  fetalHeartRate: data.fetalHeartRate || null,
  fundalHeight: data.fundalHeight || null,
  weight: data.weight || null,
  bloodPressure: {
    systolic: data.bpSystolic || null,
    diastolic: data.bpDiastolic || null
  },
  glucoseChallengeTest: data.glucoseChallengeTest || null,
  ultrasoundFindings: data.ultrasoundFindings || null,
  symptoms: data.symptoms || [],
  medications: data.medications || [],
  doctorNotes: data.doctorNotes || null,
  nextAppointment: data.nextAppointment || null
});


const getCategoryDisplayName = (category) => {
  const names = {
    vitals: 'Vital Signs',
    bodyMeasurements: 'Body Measurements',
    sugarProfile: 'Sugar Profile',
    liverProfile: 'Liver Profile',
    kidneyProfile: 'Kidney Profile',
    lipidProfile: 'Lipid Profile',
    cycleTracking: 'Cycle Tracking',
    pregnancyTracking: 'Pregnancy Tracking'
  };
  return names[category] || category;
};

const getErrorMessage = (error) => {
  if (error.message) {
    return error.message;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  return 'An unexpected error occurred. Please try again.';
};

const showMessage = (message, type) => {
  if (type === 'success' && window.showSuccessMessage) {
    window.showSuccessMessage(message);
  } else if (type === 'error' && window.showErrorMessage) {
    window.showErrorMessage(message);
  } else {
    alert(message);
  }
};

const handleDeleteRecord = async (record) => {
  if (!window.confirm('Are you sure you want to delete this record?')) {
    return;
  }
  
  console.log('Deleting record:', record);
  
  try {
    const healthLogId = record.healthlog_id;
    if (!healthLogId) {
      throw new Error('Health log ID not found');
    }
    
    // Obtener el health log completo
    const response = await HealthLogService.getHealthLogById(healthLogId);
    const healthLog = response.data;
    
    if (!healthLog) {
      throw new Error('Health log not found');
    }

    // Determinar qué array y índice actualizar basado en la categoría
    let arrayName = '';
    let recordIndex = -1;
    
    switch (selectedCategory) {
      case 'vitals':
        arrayName = 'vitals';
        recordIndex = parseInt(record.vital_id.split('_vital_')[1]);
        break;
      case 'bodyMeasurements':
        arrayName = 'bodyMeasurements';
        recordIndex = parseInt(record.measurement_id.split('_measurement_')[1]);
        break;
      case 'sugarProfile':
        arrayName = 'sugarProfile';
        recordIndex = parseInt(record.sugar_id.split('_sugar_')[1]);
        break;
      case 'liverProfile':
        arrayName = 'liverProfile';
        recordIndex = parseInt(record.liver_id.split('_liver_')[1]);
        break;
      case 'kidneyProfile':
        arrayName = 'kidneyProfile';
        recordIndex = parseInt(record.kidney_id.split('_kidney_')[1]);
        break;
      case 'lipidProfile':
        arrayName = 'lipidProfile';
        recordIndex = parseInt(record.lipid_id.split('_lipid_')[1]);
        break;
      case 'cycleTracking':
        arrayName = 'cycleTracking';
        recordIndex = parseInt(record.cycle_id.split('_cycle_')[1]);
        break;
      case 'pregnancyTracking':
        arrayName = 'pregnancyTracking';
        recordIndex = parseInt(record.pregnancy_id.split('_pregnancy_')[1]);
        break;
      default:
        throw new Error('Unknown category');
    }
    
    // Eliminar el registro del array correspondiente
    if (healthLog[arrayName] && healthLog[arrayName][recordIndex]) {
      healthLog[arrayName].splice(recordIndex, 1);
      
      // Actualizar el health log
      const result = await HealthLogService.updateHealthLog(healthLogId, {
        vitals: healthLog.vitals || [],
        bodyMeasurements: healthLog.bodyMeasurements || [],
        sugarProfile: healthLog.sugarProfile || [],
        liverProfile: healthLog.liverProfile || [],
        kidneyProfile: healthLog.kidneyProfile || [],
        lipidProfile: healthLog.lipidProfile || [],
        cycleTracking: healthLog.cycleTracking || [],
        pregnancyTracking: healthLog.pregnancyTracking || []
      });
      
      console.log('Record deleted successfully:', result);
      
      // Recargar datos
      await loadHealthLogs();
      
      if (window.showSuccessMessage) {
        window.showSuccessMessage('Record deleted successfully!');
      } else {
        alert('Record deleted successfully!');
      }
    } else {
      throw new Error(`Record not found at index ${recordIndex} in ${arrayName}`);
    }
    
  } catch (error) {
    console.error('Error deleting record:', error);
    alert(`Error deleting record: ${error.message}`);
  }
};

  const handleAddNew = () => {
  console.log('Add New button clicked - opening universal form for category:', selectedCategory);
  setShowFilterOffcanvas(false);
  setShowViewOffcanvas(false);
  setShowAddOffcanvas(false); // Cerrar el form viejo si está abierto
  
  // Abrir el formulario universal para la categoría seleccionada
  openForm(selectedCategory);
};

  // **FUNCIÓN PARA MANEJAR LA CREACIÓN DE VITAL**
  const handleSubmitNewVital = async (vitalData) => {
    console.log('=== SUBMITTING NEW VITAL ===');
    console.log('Original vital data:', vitalData);
    
    setIsSubmitting(true);
    
    try {
      const currentUser = getCurrentUser();
      const currentEcosystem = getCurrentEcosystem();
      
      console.log('Current user:', currentUser);
      console.log('Current ecosystem:', currentEcosystem);
      
      if (!currentUser || !currentUser.id) {
        throw new Error('No se pudo obtener información del usuario actual. Por favor, inicia sesión nuevamente.');
      }
      
      if (!patientId) {
        throw new Error('No se pudo obtener el ID del paciente.');
      }

      // Check if patient already has a health log
      let existingHealthLog = null;
      try {
        const response = await HealthLogService.getHealthLogsByUser(patientId);
        if (response.data && response.data.length > 0) {
          existingHealthLog = response.data[0]; // Use the first health log
        }
      } catch (error) {
        console.log('No existing health log found, will create new one');
      }

      const vitalEntry = {
        bloodPressure: {
          systolic: vitalData.systolic || null,
          diastolic: vitalData.diastolic || null
        },
        heartRate: vitalData.heartRate || null,
        temperature: vitalData.temperature || null,
        oxygenSaturation: vitalData.oxygenSaturation || null,
        respiratoryRate: vitalData.respiratoryRate || null
      };

      let result;
      if (existingHealthLog) {
        // Update existing health log by adding new vital
        result = await HealthLogService.updateVitals(existingHealthLog._id, vitalEntry);
      } else {
        // Create new health log with vital
        const healthLogData = {
          userId: patientId,
          vitals: [vitalEntry]
        };
        result = await HealthLogService.createHealthLog(healthLogData);
      }
      
      console.log('Vital created successfully:', result);
      
      await loadHealthLogs();
      await loadHealthLogStats();
      
      setShowAddOffcanvas(false);
      
      if (window.showSuccessMessage) {
        window.showSuccessMessage('Vital signs recorded successfully!');
      } else {
        alert('Vital signs recorded successfully!');
      }
      
    } catch (error) {
      console.error('Error creating vital:', error);
      
      let errorMessage = 'Error recording vital signs. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      alert(`Error: ${errorMessage}`);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

const handleUpdateVital = async (updatedData) => {
  console.log('=== UPDATING VITAL ===');
  console.log('Updated vital data:', updatedData);
  console.log('Editing vital ID:', editingHealthLog?.vital_id);
  
  setIsSubmitting(true);
  
  try {
    if (!editingHealthLog?.healthlog_id) {
      throw new Error('No se pudo obtener el ID del health log a actualizar');
    }

    // Obtener el health log completo
    const response = await HealthLogService.getHealthLogById(editingHealthLog.healthlog_id);
    const healthLog = response.data;
    
    if (!healthLog) {
      throw new Error('No se encontró el health log');
    }

    // Encontrar y actualizar el vital específico
    const vitalId = editingHealthLog.vital_id;
    const vitalIndex = parseInt(vitalId.split('_vital_')[1]);
    
    console.log('Updating vital at index:', vitalIndex);
    
    if (healthLog.vitals && healthLog.vitals[vitalIndex]) {
      // Actualizar el vital específico manteniendo la estructura original
      const updatedVital = {
        ...healthLog.vitals[vitalIndex], // Mantener datos existentes
        bloodPressure: {
          systolic: updatedData.systolic || null,
          diastolic: updatedData.diastolic || null
        },
        heartRate: updatedData.heartRate || null,
        temperature: updatedData.temperature || null,
        oxygenSaturation: updatedData.oxygenSaturation || null,
        respiratoryRate: updatedData.respiratoryRate || null
      };
      
      // Actualizar el vital en el array
      healthLog.vitals[vitalIndex] = updatedVital;
      
      // Actualizar el health log completo
      const result = await HealthLogService.updateHealthLog(editingHealthLog.healthlog_id, {
        vitals: healthLog.vitals,
        bodyMeasurements: healthLog.bodyMeasurements || [],
        sugarProfile: healthLog.sugarProfile || [],
        liverProfile: healthLog.liverProfile || [],
        kidneyProfile: healthLog.kidneyProfile || [],
        lipidProfile: healthLog.lipidProfile || [],
        cycleTracking: healthLog.cycleTracking || [],
        pregnancyTracking: healthLog.pregnancyTracking || []
      });
      
      console.log('Vital updated successfully:', result);
      
      // Recargar datos
      await loadHealthLogs();
      await loadHealthLogStats();
      
      setShowEditOffcanvas(false);
      setEditingHealthLog(null);
      
      if (window.showSuccessMessage) {
        window.showSuccessMessage('Vital signs updated successfully!');
      } else {
        alert('Vital signs updated successfully!');
      }
    } else {
      throw new Error(`No se encontró el vital en el índice ${vitalIndex}`);
    }
    
  } catch (error) {
    console.error('Error updating vital:', error);
    
    let errorMessage = 'Error updating vital signs. Please try again.';
    
    if (error.message) {
      errorMessage = error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    }
    
    alert(`Error: ${errorMessage}`);
    throw error;
  } finally {
    setIsSubmitting(false);
  }
};

  // **FUNCIONES AUXILIARES PARA OBTENER DATOS DEL USUARIO**
  const getCurrentUser = () => {
    console.log('Getting current user...');
    
    try {
      const userData = localStorage.getItem('currentUser') || localStorage.getItem('user') || localStorage.getItem('authUser');
      if (userData) {
        const user = JSON.parse(userData);
        console.log('User found in localStorage:', user);
        return {
          id: user._id || user.id || user.userId,
          name: user.name || user.username || user.fullName,
          email: user.email
        };
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }

    try {
      const sessionUserData = sessionStorage.getItem('currentUser') || sessionStorage.getItem('user');
      if (sessionUserData) {
        const user = JSON.parse(sessionUserData);
        console.log('User found in sessionStorage:', user);
        return {
          id: user._id || user.id || user.userId,
          name: user.name || user.username || user.fullName,
          email: user.email
        };
      }
    } catch (error) {
      console.error('Error parsing user data from sessionStorage:', error);
    }

    console.warn('No user data found, using default values for testing');
    return {
      id: '507f1f77bcf86cd799439011',
      name: 'Test User',
      email: 'test@example.com'
    };
  };

  const getCurrentEcosystem = () => {
    console.log('Getting current ecosystem...');
    
    try {
      const ecosystemData = localStorage.getItem('currentEcosystem') || localStorage.getItem('ecosystem');
      if (ecosystemData) {
        const ecosystem = JSON.parse(ecosystemData);
        console.log('Ecosystem found in localStorage:', ecosystem);
        return {
          id: ecosystem._id || ecosystem.id || ecosystem.ecosystemId,
          name: ecosystem.name || ecosystem.ecosystemName
        };
      }
    } catch (error) {
      console.error('Error parsing ecosystem data from localStorage:', error);
    }

    console.warn('No ecosystem data found, using default values for testing');
    return {
      id: '507f1f77bcf86cd799439012',
      name: 'Test Ecosystem'
    };
  };

  // 1. FUNCIÓN PARA ABRIR EL FILTRO OFFCANVAS
  const handleOpenFilterOffcanvas = () => {
    console.log('Opening filter offcanvas');
    setShowFilterOffcanvas(true);
  };

  // 2. FUNCIÓN PARA OBTENER FILTROS ACTIVOS
  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (filters.bloodPressureRange && filters.bloodPressureRange !== 'All') {
      activeFilters.push({
        key: 'bloodPressureRange',
        label: `BP: ${filters.bloodPressureRange}`,
        value: filters.bloodPressureRange
      });
    }
    
    if (filters.heartRateRange && filters.heartRateRange !== 'All') {
      activeFilters.push({
        key: 'heartRateRange',
        label: `HR: ${filters.heartRateRange}`,
        value: filters.heartRateRange
      });
    }
    
    if (filters.temperatureRange && filters.temperatureRange !== 'All') {
      activeFilters.push({
        key: 'temperatureRange',
        label: `Temp: ${filters.temperatureRange}`,
        value: filters.temperatureRange
      });
    }
    
    if (filters.dateFrom) {
      activeFilters.push({
        key: 'dateFrom',
        label: `From: ${formatDate(filters.dateFrom)}`,
        value: filters.dateFrom
      });
    }
    
    if (filters.dateTo) {
      activeFilters.push({
        key: 'dateTo',
        label: `To: ${formatDate(filters.dateTo)}`,
        value: filters.dateTo
      });
    }
    
    if (globalFilter && globalFilter.trim()) {
      activeFilters.push({
        key: 'globalFilter',
        label: `Search: "${globalFilter}"`,
        value: globalFilter
      });
    }
    return activeFilters;
  };


// **FUNCIONES AUXILIARES PARA CATEGORIZACIÓN**
  const getVitalStatus = (vital) => {
    // Check if vital has any data
    const hasData = vital.bloodPressure?.systolic || vital.bloodPressure?.diastolic || 
                   vital.heartRate || vital.temperature || vital.oxygenSaturation || vital.respiratoryRate;
    return hasData ? '1' : '0';
  };

  const getBPCategory = (systolic, diastolic) => {
    if (!systolic || !diastolic) return 'Not recorded';
    
    if (systolic < 90 || diastolic < 60) return 'Low';
    if (systolic < 120 && diastolic < 80) return 'Normal';
    if (systolic < 130 && diastolic < 80) return 'Elevated';
    if (systolic < 140 || diastolic < 90) return 'Stage 1 High';
    return 'Stage 2 High';
  };

  const getHRCategory = (heartRate) => {
    if (!heartRate) return 'Not recorded';
    
    if (heartRate < 60) return 'Low';
    if (heartRate <= 100) return 'Normal';
    return 'High';
  };

  const getTempCategory = (temperature) => {
    if (!temperature) return 'Not recorded';
    
    if (temperature < 36.0) return 'Low';
    if (temperature <= 37.5) return 'Normal';
    if (temperature <= 38.5) return 'Fever';
    return 'High Fever';
  };

  const getOxygenSaturationColor = (oxygenSat) => {
    if (!oxygenSat) return 'secondary';
    
    if (oxygenSat >= 95) return 'success';
    if (oxygenSat >= 90) return 'warning';
    return 'danger';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Time';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case '1':
        return 'success';
      case '0':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case '1':
        return 'Complete';
      case '0':
        return 'Incomplete';
      default:
        return 'Unknown';
    }
  };

  // **FUNCIONES DE MANEJO DE FILTROS**
  const handleRemoveFilter = (filterToRemove) => {
    console.log('Removing filter:', filterToRemove);
    
    if (filterToRemove.key === 'globalFilter') {
      setGlobalFilter('');
    } else if (filterToRemove.key === 'status') {
      setFilters(prev => ({ ...prev, status: 'All' }));
    } else {
      setFilters(prev => ({ ...prev, [filterToRemove.key]: filterToRemove.key.includes('Range') ? 'All' : '' }));
    }
  };

  const handleClearAllActiveFilters = () => {
    console.log('Clearing all active filters');
    
    setFilters({
      bloodPressureRange: 'All',
      heartRateRange: 'All',
      temperatureRange: 'All',
      dateFrom: '',
      dateTo: '',
      status: 'All'
    });
    
    setGlobalFilter('');
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      bloodPressureRange: 'All',
      heartRateRange: 'All',
      temperatureRange: 'All',
      dateFrom: '',
      dateTo: '',
      status: 'All'
    });
  };

  const handleCloseViewOffcanvas = () => {
    console.log('Closing view offcanvas');
    setShowViewOffcanvas(false);
    setSelectedHealthLog(null);
  };

  const handleCloseAddOffcanvas = () => {
    console.log('Closing add offcanvas');
    if (!isSubmitting) {
      setShowAddOffcanvas(false);
    }
  };

  const handleCloseEditOffcanvas = () => {
    console.log('Closing edit offcanvas');
    if (!isSubmitting) {
      setShowEditOffcanvas(false);
      setEditingHealthLog(null);
    }
  };
// 1. ARRAY DE CATEGORÍAS CON METADATA
const healthCategories = [
  {
    key: 'vitals',
    name: 'Vital Signs',
    icon: 'FiHeart',
    color: 'info',
    description: 'Blood pressure, heart rate, temperature'
  },
  {
    key: 'bodyMeasurements',
    name: 'Body Measurements',
    icon: 'FiUser',
    color: 'info',
    description: 'Height, weight, BMI, head circumference'
  },
  {
    key: 'sugarProfile',
    name: 'Sugar Profile',
    icon: 'FiDroplet',
    color: 'success',
    description: 'Blood sugar, HbA1c levels'
  },
  {
    key: 'liverProfile',
    name: 'Liver Profile',
    icon: 'FiShield',
    color: 'success',
    description: 'ALT, AST, bilirubin, albumin'
  },
  {
    key: 'kidneyProfile',
    name: 'Kidney Profile',
    icon: 'FiFilter',
    color: 'warning',
    description: 'Creatinine, urea, eGFR'
  },
  {
    key: 'lipidProfile',
    name: 'Lipid Profile',
    icon: 'FiTrendingUp',
    color: 'warning',
    description: 'Cholesterol, LDL, HDL, triglycerides'
  },
  {
    key: 'cycleTracking',
    name: 'Menstrual Cycle',
    icon: 'FiCalendar',
    color: 'danger',
    description: 'Cycle tracking, symptoms, mood'
  },
  {
    key: 'pregnancyTracking',
    name: 'Pregnancy',
    icon: 'FaBaby', 
    color: 'danger',
    description: 'Gestational tracking, fetal monitoring'
  }
];

const ViewRecordOffcanvas = ({ isOpen, record, category, onClose, onEdit, onDelete }) => {
  if (!isOpen || !record) return null;

  const getCategoryTitle = (cat) => {
    const titles = {
      vitals: 'Vital Signs',
      bodyMeasurements: 'Body Measurements', 
      sugarProfile: 'Sugar Profile',
      liverProfile: 'Liver Profile',
      kidneyProfile: 'Kidney Profile',
      lipidProfile: 'Lipid Profile',
      cycleTracking: 'Menstrual Cycle',
      pregnancyTracking: 'Pregnancy Tracking'
    };
    return titles[cat] || 'Health Record';
  };

  const renderRecordDetails = () => {
    switch (category) {
      case 'vitals':
        return (
          <div className="row g-3">
            <div className="col-6">
              <small className="text-muted">Blood Pressure</small>
              <div className="fw-bold">
                {record.systolic && record.diastolic 
                  ? `${record.systolic}/${record.diastolic} mmHg` 
                  : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Heart Rate</small>
              <div className="fw-bold">
                {record.heart_rate ? `${record.heart_rate} bpm` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Temperature</small>
              <div className="fw-bold">
                {record.temperature ? `${record.temperature}°C` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Oxygen Saturation</small>
              <div className="fw-bold">
                {record.oxygen_saturation ? `${record.oxygen_saturation}%` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Respiratory Rate</small>
              <div className="fw-bold">
                {record.respiratory_rate ? `${record.respiratory_rate}/min` : 'Not recorded'}
              </div>
            </div>
          </div>
        );

      case 'bodyMeasurements':
        return (
          <div className="row g-3">
            <div className="col-6">
              <small className="text-muted">Height</small>
              <div className="fw-bold">
                {record.height ? `${record.height} cm` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Weight</small>
              <div className="fw-bold">
                {record.weight ? `${record.weight} kg` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">BMI</small>
              <div className="fw-bold">
                {record.bmi || 'Not calculated'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Head Circumference</small>
              <div className="fw-bold">
                {record.head_circumference ? `${record.head_circumference} cm` : 'Not recorded'}
              </div>
            </div>
          </div>
        );

      case 'sugarProfile':
        return (
          <div className="row g-3">
            <div className="col-6">
              <small className="text-muted">Fasting Blood Sugar</small>
              <div className="fw-bold">
                {record.fasting_blood_sugar ? `${record.fasting_blood_sugar} mg/dL` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Post-meal Sugar</small>
              <div className="fw-bold">
                {record.postprandial_sugar ? `${record.postprandial_sugar} mg/dL` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">HbA1c</small>
              <div className="fw-bold">
                {record.hba1c ? `${record.hba1c}%` : 'Not recorded'}
              </div>
            </div>
          </div>
        );

      case 'liverProfile':
        return (
          <div className="row g-3">
            <div className="col-6">
              <small className="text-muted">ALT</small>
              <div className="fw-bold">
                {record.alt ? `${record.alt} U/L` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">AST</small>
              <div className="fw-bold">
                {record.ast ? `${record.ast} U/L` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">ALP</small>
              <div className="fw-bold">
                {record.alp ? `${record.alp} U/L` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Bilirubin Total</small>
              <div className="fw-bold">
                {record.bilirubin_total ? `${record.bilirubin_total} mg/dL` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Albumin</small>
              <div className="fw-bold">
                {record.albumin ? `${record.albumin} g/dL` : 'Not recorded'}
              </div>
            </div>
          </div>
        );

      case 'kidneyProfile':
        return (
          <div className="row g-3">
            <div className="col-6">
              <small className="text-muted">Creatinine</small>
              <div className="fw-bold">
                {record.creatinine ? `${record.creatinine} mg/dL` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Urea</small>
              <div className="fw-bold">
                {record.urea ? `${record.urea} mg/dL` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">eGFR</small>
              <div className="fw-bold">
                {record.egfr ? `${record.egfr} mL/min` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Sodium</small>
              <div className="fw-bold">
                {record.sodium ? `${record.sodium} mEq/L` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Potassium</small>
              <div className="fw-bold">
                {record.potassium ? `${record.potassium} mEq/L` : 'Not recorded'}
              </div>
            </div>
          </div>
        );

      case 'lipidProfile':
        return (
          <div className="row g-3">
            <div className="col-6">
              <small className="text-muted">Total Cholesterol</small>
              <div className="fw-bold">
                {record.total_cholesterol ? `${record.total_cholesterol} mg/dL` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">LDL</small>
              <div className="fw-bold">
                {record.ldl ? `${record.ldl} mg/dL` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">HDL</small>
              <div className="fw-bold">
                {record.hdl ? `${record.hdl} mg/dL` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Triglycerides</small>
              <div className="fw-bold">
                {record.triglycerides ? `${record.triglycerides} mg/dL` : 'Not recorded'}
              </div>
            </div>
          </div>
        );

      case 'cycleTracking':
        return (
          <div className="row g-3">
            <div className="col-6">
              <small className="text-muted">Cycle Length</small>
              <div className="fw-bold">
                {record.cycle_length ? `${record.cycle_length} days` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Flow Level</small>
              <div className="fw-bold">
                {record.flow_level || 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Ovulation Date</small>
              <div className="fw-bold">
                {formatDate(record.ovulation_date)}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Expected Next Period</small>
              <div className="fw-bold">
                {formatDate(record.expected_next_period)}
              </div>
            </div>
            <div className="col-12">
              <small className="text-muted">Symptoms</small>
              <div className="fw-bold">
                {record.symptoms && record.symptoms.length > 0 
                  ? record.symptoms.join(', ') 
                  : 'None reported'}
              </div>
            </div>
          </div>
        );

      case 'pregnancyTracking':
        return (
          <div className="row g-3">
            <div className="col-6">
              <small className="text-muted">Gestational Age</small>
              <div className="fw-bold">
                {record.gestational_age_weeks ? `${record.gestational_age_weeks} weeks` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Fetal Heart Rate</small>
              <div className="fw-bold">
                {record.fetal_heart_rate ? `${record.fetal_heart_rate} bpm` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Weight</small>
              <div className="fw-bold">
                {record.weight ? `${record.weight} kg` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Blood Pressure</small>
              <div className="fw-bold">
                {record.bp_systolic && record.bp_diastolic 
                  ? `${record.bp_systolic}/${record.bp_diastolic} mmHg` 
                  : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Fundal Height</small>
              <div className="fw-bold">
                {record.fundal_height ? `${record.fundal_height} cm` : 'Not recorded'}
              </div>
            </div>
            <div className="col-6">
              <small className="text-muted">Next Appointment</small>
              <div className="fw-bold">
                {formatDate(record.next_appointment)}
              </div>
            </div>
          </div>
        );

      default:
        return <div>No details available</div>;
    }
  };

  return (
    <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', zIndex: 1050, width: '500px' }}>
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">{getCategoryTitle(category)} Details</h5>
        <button
          type="button"
          className="btn-close"
          onClick={onClose}
        />
      </div>
      <div className="offcanvas-body">
        <div className="card border-0 bg-light mb-3">
          <div className="card-body">
            <h6 className="card-title text-primary">
              <FiShield className="me-2" />
              {getCategoryTitle(category)} Record
            </h6>
            {renderRecordDetails()}
          </div>
        </div>

        {/* Record Information */}
        <div className="mt-3">
          <h6>Record Information</h6>
          <div className="row g-2">
            <div className="col-6">
              <small className="text-muted">Recorded Date</small>
              <div>{formatDate(record.created_date)}</div>
            </div>
            <div className="col-6">
              <small className="text-muted">Status</small>
              <div>
                <span className={`badge bg-${getStatusColor(record.status)}`}>
                  {getStatusText(record.status)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4">
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-primary flex-grow-1"
              onClick={() => {
                onClose();
                onEdit(record);
              }}
            >
              <FiEdit3 className="me-1" /> Edit
            </button>
            <button
              className="btn btn-outline-danger"
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this record?')) {
                  onDelete(record);
                  onClose();
                }
              }}
            >
              <FiTrash2 />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 2. COMPONENTE DE NAVEGACIÓN POR CATEGORÍAS
const CategoryNavigation = ({ 
  selectedCategory, 
  onCategoryChange, 
  healthData, 
  isLoading 
}) => {
  return (
    <div className="row mb-4">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h6 className="card-title mb-3">
              <FiActivity className="me-2" />
              Health Data Categories
            </h6>
            
            <div className="row g-3">
              {healthCategories.map((category) => {
                const count = healthData[category.key]?.length || 0;
                const isActive = selectedCategory === category.key;
                
                return (
                  <div key={category.key} className="col-md-6 col-lg-3">
                    <div
                      className={`card h-100 cursor-pointer border-2 ${
                        isActive ? `border-${category.color} bg-${category.color} bg-opacity-10` : 'border-light'
                      }`}
                      onClick={() => onCategoryChange(category.key)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="card-body text-center">
                        <div className={`mb-2 text-${category.color}`}>
                          <i className={`fas fa-heart`} style={{ fontSize: '2rem' }}></i>
                        </div>
                        
                        <h6 className="card-title mb-1">{category.name}</h6>
                        
                        <div className={`badge bg-${category.color} mb-2`}>
                          {isLoading ? '...' : count} records
                        </div>
                        
                        <p className="card-text small text-muted mb-0">
                          {category.description}
                        </p>
                        
                        {/* Agregar indicador de datos */}
                        {count > 0 && (
                          <div className="mt-1">
                            <small className="text-success">
                              <FiCheck className="me-1" />
                              Has data
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

  // **RENDER DEL COMPONENTE**
  return (
    <main id="app">
    <TopHeader />
    <div className="container-fluid w-100">
      <PatientManagement activeMenu='/my-vitals'/>
      
      {/* Breadcrumb */}
      <div className="mb-3">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
            <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
            <li className="breadcrumb-item active" aria-current="page">
              My Vitals {patientData?.patientName ? `- ${patientData.patientName}` : ''}
            </li>
          </ol>
        </nav>
      </div>
{/* AGREGAR NAVEGACIÓN POR CATEGORÍAS */}
      <CategoryNavigation
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        healthData={allHealthData}
        isLoading={isLoading}
      />
<div className="card-body">
        {/* Título dinámico */}
        <div className="d-flex align-items-center mb-3">
          <h5 className="mb-0">
            {healthCategories.find(cat => cat.key === selectedCategory)?.name || 'Health Data'}
            {patientData?.patientName && (
              <span className="text-muted"> - {patientData.patientName}</span>
            )}
          </h5>
          <span className={`badge bg-primary ms-2`}>
            {filteredHealthLogs.length} records
          </span>
        </div>
        </div>

<div className="card-body">
          {/* Enhanced Search and Actions Bar */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                
                {/* Left side - Search */}
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search"
                      value={globalFilter}
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                    />
                  </div>
                </div>

            {/* Right side - Controls */}
                            <div className="d-flex align-items-center gap-2">
                              {/* Sort By Dropdown */}
                              <div className="dropdown" style={{position: 'relative'}}>
                                <button
                                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowSortDropdown(!showSortDropdown);
                                  }}
                                >
                                  {getSortDisplayText()}
                                  <FiChevronDown />
                                </button>
                                {showSortDropdown && (
                                  <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                                    <button
                                      className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                                      onClick={() => handleSortChange('newest')}
                                    >
                                      Newest First
                                    </button>
                                    <button
                                      className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                                      onClick={() => handleSortChange('oldest')}
                                    >
                                      Oldest First
                                    </button>
                                    <button
  className={`dropdown-item ${sortBy === 'bp_high' ? 'active' : ''}`}
  onClick={() => handleSortChange('bp_high')}
>
  By Blood Pressure
</button>
<button
  className={`dropdown-item ${sortBy === 'heart_rate' ? 'active' : ''}`}
  onClick={() => handleSortChange('heart_rate')}
>
  By Heart Rate
</button>
                                  </div>
                                )}
                              </div>
            
                              {/* Filter Button */}
                              <button
                                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                                onClick={handleOpenFilterOffcanvas}
                              >
                                <FiFilter />
                                Filter
                              </button>
                        
                              {/* Add New Button */}
                              <button
                                className="btn btn-primary d-flex align-items-center gap-2"
                                onClick={handleAddNew}
                              >
                                <FiPlus />
                                Add New
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
            
                      {/* Active Filters Display */}
                      {getActiveFilters().length > 0 && (
                        <div className="row mb-3">
                          <div className="col-12">
                            <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                              <span className="text-muted fw-medium">Active filters:</span>
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                {getActiveFilters().map((filter) => (
                                  <span
                                    key={filter.key}
                                    className="badge bg-primary d-flex align-items-center gap-1"
                                    style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                                  >
                                    {filter.label}
                                    <button
                                      type="button"
                                      className="btn-close btn-close-white"
                                      style={{ 
                                        fontSize: '0.625rem',
                                        padding: '0.125rem',
                                        marginLeft: '0.25rem'
                                      }}
                                      onClick={() => handleRemoveFilter(filter)}
                                      title={`Remove ${filter.label} filter`}
                                    ></button>
                                  </span>
                                ))}
                                
                                {/* Clear All Button */}
                                {getActiveFilters().length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                    onClick={handleClearAllActiveFilters}
                                    title="Clear all filters"
                                  >
                                    <FiX size={14} />
                                    Clear All
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

              {/* Table */}
              
                {/* Main Table */}
                            <MaterialReactTable
                              columns={getColumnsByCategory(selectedCategory)} // Usar columnas dinámicas
          data={filteredHealthLogs}
                
                              
                              // Configuración básica
                              enableRowSelection
                              enableColumnFilters
                              enableGlobalFiltering
                              enableSorting={false}
                              enablePagination
                              
                              // Toolbars
                              enableTopToolbar
                              enableBottomToolbar
                              
                              // Features opcionales (puedes deshabilitarlas si no las necesitas)
                              enableDensityToggle
                              enableFullScreenToggle
                              enableHiding
                              enableColumnOrdering
                              enableColumnResizing={false}  // Deshabilitar si causa problemas
                              enableGrouping={false}        // Deshabilitar si causa problemas
                              
                              // Estados
                              initialState={{
                                pagination: { pageSize: 10, pageIndex: 0 },
                                sorting: [{ id: 'vaccine_name', desc: false }], // Cambiar a columna existente
                                density: 'comfortable',
                                showGlobalFilter: false
                              }}
                              
                              state={{
                                isLoading,
                                rowSelection,
                                globalFilter
                              }}
                              
                              // Callbacks
                              onRowSelectionChange={setRowSelection}
                              onGlobalFilterChange={handleGlobalFilterChange}
                              
                              // Configuración de renderizado
                              muiTableContainerProps={{
                                sx: { maxHeight: '600px' }
                              }}
                />
            {/* Results Summary */}
                      {filteredHealthLogs.length > 0 && (
  <div className="mt-3">
    <small className="text-muted">
Showing {filteredHealthLogs.length} of {healthLogs.length} vital records

                            {Object.values(filters).some(f => f && f !== 'All') || globalFilter ? ' (filtered)' : ''}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', zIndex: 1050 }}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Filter & Search</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowFilterOffcanvas(false)}
            />
          </div>
          <div className="offcanvas-body">
            {/* Global Search */}
            <div className="mb-3">
              <label className="form-label">Search</label>
              <div className="input-group">
                <span className="input-group-text">
                  <FiSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search vaccines, manufacturers, facilities..."
                  value={globalFilter}
                  onChange={(e) => handleGlobalFilterChange(e.target.value)}
                />
              </div>
            </div>

            <hr />

            {/* Specific Filters */}
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
  className="form-select"
  value={filters.bloodPressureRange}
  onChange={(e) => handleFilterChange('bloodPressureRange', e.target.value)}
>
  <option value="All">All Blood Pressure</option>
  <option value="Low">Low</option>
  <option value="Normal">Normal</option>
  <option value="Elevated">Elevated</option>
  <option value="High">High</option>
</select>
            </div>

            <div className="mb-3">
  <label className="form-label">Heart Rate Range</label>
  <select
    className="form-select"
    value={filters.heartRateRange}
    onChange={(e) => handleFilterChange('heartRateRange', e.target.value)}
  >
    <option value="All">All Heart Rates</option>
    <option value="Low">Low ({"<"} 60 bpm)</option>
    <option value="Normal">Normal (60-100 bpm)</option>
    <option value="High">High ({">"} 100 bpm)</option>
  </select>
</div>

<div className="mb-3">
  <label className="form-label">Temperature Range</label>
  <select
    className="form-select"
    value={filters.temperatureRange}
    onChange={(e) => handleFilterChange('temperatureRange', e.target.value)}
  >
    <option value="All">All Temperatures</option>
    <option value="Low">Low ({"<"} 36°C)</option>
    <option value="Normal">Normal (36-37.5°C)</option>
    <option value="Fever">Fever ({">"} 37.5°C)</option>
  </select>
</div>

            <div className="mb-3">
              <label className="form-label">Date From</label>
              <input
                type="date"
                className="form-control"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Date To</label>
              <input
                type="date"
                className="form-control"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-primary flex-grow-1"
                onClick={handleApplyFilters}
              >
                Apply Filters
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Offcanvas */}
<ViewRecordOffcanvas
  isOpen={showViewOffcanvas}
  record={selectedHealthLog}
  category={selectedCategory}
  onClose={handleCloseViewOffcanvas}
  onEdit={handleEditRecord}
  onDelete={handleDeleteRecord}
/>


      {/* Add New Health Log Offcanvas */}
      {showAddOffcanvas && (
  <AddVitalForm
    isOpen={showAddOffcanvas}
    onClose={handleCloseAddOffcanvas}
    onSubmit={handleSubmitNewVital}
    isSubmitting={isSubmitting}
    patientData={patientData}
  />
)}

{showEditOffcanvas && editingHealthLog && (
  <EditVitalForm
    isOpen={showEditOffcanvas}
    onClose={handleCloseEditOffcanvas}
    onSubmit={handleUpdateVital}
    vitalData={editingHealthLog}
    isSubmitting={isSubmitting}
    patientData={patientData}
  />
)}
{/* Universal Health Form */}
{formState.isOpen && (
  <UniversalHealthForm
    isOpen={formState.isOpen}
    onClose={closeForm}
    onSubmit={handleUniversalFormSubmit}
    category={formState.category}
    editData={formState.editData}
    isSubmitting={formState.isSubmitting}
    patientData={patientData}
  />
)}

      {/* Backdrop for offcanvas */}
      {(showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showEditOffcanvas || formState.isOpen) && (
  <div 
    className="offcanvas-backdrop fade show" 
    onClick={() => {
      if (!isSubmitting && !formState.isSubmitting) {
        setShowFilterOffcanvas(false);
        setShowAddOffcanvas(false);
        setShowViewOffcanvas(false);
        setShowEditOffcanvas(false);
        closeForm();
      }
    }}
  />
)}
     {/* Patient Clinical Data Component */}
      <PatientMedicalClinicalData 
        patientData={patientData}
        alertInfo={alertInfo}
      />
    </main>
  );
};

// AddVitalForm Component
const AddVitalForm = ({ isOpen, onClose, onSubmit, isSubmitting, patientData }) => {
  const [formData, setFormData] = useState({
    systolic: '',
    diastolic: '',
    heartRate: '',
    temperature: '',
    oxygenSaturation: '',
    respiratoryRate: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // At least one vital sign should be provided
    const hasAnyVital = formData.systolic || formData.diastolic || 
                       formData.heartRate || formData.temperature || 
                       formData.oxygenSaturation || formData.respiratoryRate;
    
    if (!hasAnyVital) {
      newErrors.general = 'Please provide at least one vital sign measurement';
    }

    // Validate blood pressure - if one is provided, both should be provided
    if ((formData.systolic && !formData.diastolic) || (!formData.systolic && formData.diastolic)) {
      newErrors.bloodPressure = 'Please provide both systolic and diastolic readings';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Convert string values to numbers where appropriate
      const cleanData = {
        systolic: formData.systolic ? parseInt(formData.systolic) : null,
        diastolic: formData.diastolic ? parseInt(formData.diastolic) : null,
        heartRate: formData.heartRate ? parseInt(formData.heartRate) : null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        oxygenSaturation: formData.oxygenSaturation ? parseInt(formData.oxygenSaturation) : null,
        respiratoryRate: formData.respiratoryRate ? parseInt(formData.respiratoryRate) : null
      };

      await onSubmit(cleanData);
      
      // Reset form on success
      setFormData({
        systolic: '',
        diastolic: '',
        heartRate: '',
        temperature: '',
        oxygenSaturation: '',
        respiratoryRate: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', zIndex: 1050, width: '500px' }}>
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">Add New Vital Signs</h5>
        <button
          type="button"
          className="btn-close"
          onClick={onClose}
          disabled={isSubmitting}
        />
      </div>
      <div className="offcanvas-body">
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            {/* Patient Info */}
            <div className="col-12">
              <div className="alert alert-info">
                <FiUser className="me-2" />
                Recording vital signs for: <strong>{patientData?.patientName}</strong>
              </div>
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="col-12">
                <div className="alert alert-danger">{errors.general}</div>
              </div>
            )}

            {/* Blood Pressure */}
            <div className="col-12">
              <label className="form-label">Blood Pressure (mmHg)</label>
              <div className="row">
                <div className="col-6">
                  <input
                    type="number"
                    className={`form-control ${errors.bloodPressure ? 'is-invalid' : ''}`}
                    placeholder="Systolic"
                    value={formData.systolic}
                    onChange={(e) => handleInputChange('systolic', e.target.value)}
                    disabled={isSubmitting}
                    min="0"
                    max="300"
                  />
                </div>
                <div className="col-6">
                  <input
                    type="number"
                    className={`form-control ${errors.bloodPressure ? 'is-invalid' : ''}`}
                    placeholder="Diastolic"
                    value={formData.diastolic}
                    onChange={(e) => handleInputChange('diastolic', e.target.value)}
                    disabled={isSubmitting}
                    min="0"
                    max="200"
                  />
                </div>
              </div>
              {errors.bloodPressure && <div className="invalid-feedback d-block">{errors.bloodPressure}</div>}
            </div>

            {/* Heart Rate */}
            <div className="col-md-6">
              <label className="form-label">Heart Rate (bpm)</label>
              <input
                type="number"
                className="form-control"
                value={formData.heartRate}
                onChange={(e) => handleInputChange('heartRate', e.target.value)}
                placeholder="e.g., 72"
                disabled={isSubmitting}
                min="0"
                max="300"
              />
            </div>

            {/* Temperature */}
            <div className="col-md-6">
              <label className="form-label">Temperature (°C)</label>
              <input
                type="number"
                step="0.1"
                className="form-control"
                value={formData.temperature}
                onChange={(e) => handleInputChange('temperature', e.target.value)}
                placeholder="e.g., 36.5"
                disabled={isSubmitting}
                min="30"
                max="45"
              />
            </div>

            {/* Oxygen Saturation */}
            <div className="col-md-6">
              <label className="form-label">Oxygen Saturation (%)</label>
              <input
                type="number"
                className="form-control"
                value={formData.oxygenSaturation}
                onChange={(e) => handleInputChange('oxygenSaturation', e.target.value)}
                placeholder="e.g., 98"
                disabled={isSubmitting}
                min="0"
                max="100"
              />
            </div>

            {/* Respiratory Rate */}
            <div className="col-md-6">
              <label className="form-label">Respiratory Rate (/min)</label>
              <input
                type="number"
                className="form-control"
                value={formData.respiratoryRate}
                onChange={(e) => handleInputChange('respiratoryRate', e.target.value)}
                placeholder="e.g., 16"
                disabled={isSubmitting}
                min="0"
                max="100"
              />
            </div>

            {/* Action Buttons */}
            <div className="col-12">
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Recording...
                    </>
                  ) : (
                    <>
                      <FiCheck className="me-2" />
                      Record Vital Signs
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// EditVitalForm Component - Versión corregida para presión arterial
const EditVitalForm = ({ isOpen, onClose, onSubmit, vitalData, isSubmitting, patientData }) => {
  const [formData, setFormData] = useState({
    systolic: '',
    diastolic: '',
    heartRate: '',
    temperature: '',
    oxygenSaturation: '',
    respiratoryRate: ''
  });
  const [errors, setErrors] = useState({});

  // Initialize form data when vitalData changes
  useEffect(() => {
    if (vitalData) {
      console.log('🔍 COMPLETE VITAL DATA:', JSON.stringify(vitalData, null, 2));
      
      let systolicValue = '';
      let diastolicValue = '';
      let heartRate = '';
      let temperature = '';
      let oxygenSaturation = '';
      let respiratoryRate = '';
      
      // Buscar en el array vitals primero (estructura más probable según la consola)
      if (vitalData.vitals && Array.isArray(vitalData.vitals)) {
        console.log('📋 VITALS ARRAY:', vitalData.vitals);
        
        // Buscar el objeto que contenga bloodPressure
        const vitalWithBP = vitalData.vitals.find(vital => vital.bloodPressure);
        if (vitalWithBP && vitalWithBP.bloodPressure) {
          systolicValue = vitalWithBP.bloodPressure.systolic || '';
          diastolicValue = vitalWithBP.bloodPressure.diastolic || '';
          console.log('🩸 Found BP in vitals array:', { systolic: systolicValue, diastolic: diastolicValue });
        }
        
        // Buscar otros valores en el array de vitals
        vitalData.vitals.forEach(vital => {
          if (vital.heartRate && !heartRate) heartRate = vital.heartRate;
          if (vital.temperature && !temperature) temperature = vital.temperature;
          if (vital.oxygenSaturation && !oxygenSaturation) oxygenSaturation = vital.oxygenSaturation;
          if (vital.respiratoryRate && !respiratoryRate) respiratoryRate = vital.respiratoryRate;
        });
      }
      
      // Si no encontramos en vitals, buscar en recordData
      if ((!systolicValue || !diastolicValue) && vitalData.recordData) {
        console.log('📋 RECORD DATA:', JSON.stringify(vitalData.recordData, null, 2));
        
        if (vitalData.recordData.bloodPressure) {
          systolicValue = systolicValue || vitalData.recordData.bloodPressure.systolic || '';
          diastolicValue = diastolicValue || vitalData.recordData.bloodPressure.diastolic || '';
          console.log('🩸 Found BP in recordData:', { systolic: systolicValue, diastolic: diastolicValue });
        }
        
        // Otros valores de recordData
        heartRate = heartRate || vitalData.recordData.heartRate || '';
        temperature = temperature || vitalData.recordData.temperature || '';
        oxygenSaturation = oxygenSaturation || vitalData.recordData.oxygenSaturation || '';
        respiratoryRate = respiratoryRate || vitalData.recordData.respiratoryRate || '';
      }
      
      // Si aún no encontramos, buscar en displayRecord
      if ((!systolicValue || !diastolicValue) && vitalData.displayRecord?.bloodPressure) {
        systolicValue = systolicValue || vitalData.displayRecord.bloodPressure.systolic || '';
        diastolicValue = diastolicValue || vitalData.displayRecord.bloodPressure.diastolic || '';
        console.log('🩸 Found BP in displayRecord:', { systolic: systolicValue, diastolic: diastolicValue });
      }
      
      // Fallback final - valores directos
      if (!systolicValue || !diastolicValue) {
        console.log('⚠️ Using fallback direct values');
        systolicValue = systolicValue || vitalData.systolic || '';
        diastolicValue = diastolicValue || vitalData.diastolic || '';
      }
      
      // Valores directos para otros campos como fallback
      heartRate = heartRate || vitalData.heartRate || '';
      temperature = temperature || vitalData.temperature || '';
      oxygenSaturation = oxygenSaturation || vitalData.oxygenSaturation || '';
      respiratoryRate = respiratoryRate || vitalData.respiratoryRate || '';
      
      const finalFormData = {
        systolic: systolicValue.toString(),
        diastolic: diastolicValue.toString(),
        heartRate: heartRate.toString(),
        temperature: temperature.toString(),
        oxygenSaturation: oxygenSaturation.toString(),
        respiratoryRate: respiratoryRate.toString()
      };
      
      console.log('🎯 FINAL FORM DATA:', finalFormData);
      setFormData(finalFormData);
      setErrors({});
    }
  }, [vitalData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    const hasAnyVital = formData.systolic || formData.diastolic || 
                       formData.heartRate || formData.temperature || 
                       formData.oxygenSaturation || formData.respiratoryRate;
    
    if (!hasAnyVital) {
      newErrors.general = 'Please provide at least one vital sign measurement';
    }

    if ((formData.systolic && !formData.diastolic) || (!formData.systolic && formData.diastolic)) {
      newErrors.bloodPressure = 'Please provide both systolic and diastolic readings';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const cleanData = {
        systolic: formData.systolic ? parseInt(formData.systolic) : null,
        diastolic: formData.diastolic ? parseInt(formData.diastolic) : null,
        heartRate: formData.heartRate ? parseInt(formData.heartRate) : null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        oxygenSaturation: formData.oxygenSaturation ? parseInt(formData.oxygenSaturation) : null,
        respiratoryRate: formData.respiratoryRate ? parseInt(formData.respiratoryRate) : null
      };

      console.log('Submitting edit form with data:', cleanData);
      await onSubmit(cleanData);
    } catch (error) {
      console.error('Error submitting edit form:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', zIndex: 1050, width: '500px' }}>
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">Edit Vital Signs</h5>
        <button
          type="button"
          className="btn-close"
          onClick={onClose}
          disabled={isSubmitting}
        />
      </div>
      <div className="offcanvas-body">
        {/* Debug info mejorada */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-3 p-2 bg-light rounded">
            <small className="text-muted">
              <div><strong>Debug Info:</strong></div>
              <div>vitalData keys: {vitalData ? Object.keys(vitalData).join(', ') : 'No data'}</div>
              {vitalData?.vitals && (
                <div>vitals array length: {vitalData.vitals.length}</div>
              )}
              {vitalData?.vitals?.[0]?.bloodPressure && (
                <div>First vital BP: {JSON.stringify(vitalData.vitals[0].bloodPressure)}</div>
              )}
              <div>Current formData: systolic={formData.systolic}, diastolic={formData.diastolic}</div>
            </small>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            {/* Patient Info */}
            <div className="col-12">
              <div className="alert alert-info">
                <FiUser className="me-2" />
                Editing vital signs for: <strong>{patientData?.patientName}</strong>
              </div>
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="col-12">
                <div className="alert alert-danger">{errors.general}</div>
              </div>
            )}

            {/* Blood Pressure */}
            <div className="col-12">
              <label className="form-label">Blood Pressure (mmHg)</label>
              <div className="row">
                <div className="col-6">
                  <input
                    type="number"
                    className={`form-control ${errors.bloodPressure ? 'is-invalid' : ''}`}
                    placeholder="Systolic"
                    value={formData.systolic || ''}
                    onChange={(e) => handleInputChange('systolic', e.target.value)}
                    disabled={isSubmitting}
                    min="0"
                    max="300"
                  />
                </div>
                <div className="col-6">
                  <input
                    type="number"
                    className={`form-control ${errors.bloodPressure ? 'is-invalid' : ''}`}
                    placeholder="Diastolic"
                    value={formData.diastolic || ''}
                    onChange={(e) => handleInputChange('diastolic', e.target.value)}
                    disabled={isSubmitting}
                    min="0"
                    max="200"
                  />
                </div>
              </div>
              {errors.bloodPressure && <div className="invalid-feedback d-block">{errors.bloodPressure}</div>}
            </div>

            {/* Heart Rate */}
            <div className="col-md-6">
              <label className="form-label">Heart Rate (bpm)</label>
              <input
                type="number"
                className="form-control"
                value={formData.heartRate || ''}
                onChange={(e) => handleInputChange('heartRate', e.target.value)}
                placeholder="e.g., 72"
                disabled={isSubmitting}
                min="0"
                max="300"
              />
            </div>

            {/* Temperature */}
            <div className="col-md-6">
              <label className="form-label">Temperature (°C)</label>
              <input
                type="number"
                step="0.1"
                className="form-control"
                value={formData.temperature || ''}
                onChange={(e) => handleInputChange('temperature', e.target.value)}
                placeholder="e.g., 36.5"
                disabled={isSubmitting}
                min="30"
                max="45"
              />
            </div>

            {/* Oxygen Saturation */}
            <div className="col-md-6">
              <label className="form-label">Oxygen Saturation (%)</label>
              <input
                type="number"
                className="form-control"
                value={formData.oxygenSaturation || ''}
                onChange={(e) => handleInputChange('oxygenSaturation', e.target.value)}
                placeholder="e.g., 98"
                disabled={isSubmitting}
                min="0"
                max="100"
              />
            </div>

            {/* Respiratory Rate */}
            <div className="col-md-6">
              <label className="form-label">Respiratory Rate (/min)</label>
              <input
                type="number"
                className="form-control"
                value={formData.respiratoryRate || ''}
                onChange={(e) => handleInputChange('respiratoryRate', e.target.value)}
                placeholder="e.g., 16"
                disabled={isSubmitting}
                min="0"
                max="100"
              />
            </div>

            {/* Action Buttons */}
            <div className="col-12">
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FiCheck className="me-2" />
                      Update Vital Signs
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};


export default VitalsTreatment;